import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Spin,
  Button,
  Divider,
  Select,
  Table,
  InputNumber,
  Alert,
  Tag,
  Modal,
  Popconfirm,
  Space,
  Tooltip,
  Input,
  Collapse,
  Typography,
  Checkbox,
  Switch,
} from "antd";
import {
  faUpDown,
  faListCheck,
  faPenToSquare,
  faTrashCan,
  faEye,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
// INTERFACES
import { FormularyType } from "../../../interfaces/Wizard.interface";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import SelectMessagesMultiple from "../../components/select/SelectMessagesMultiple";
import GlobalAlert2 from "../../home/GlobalAlert2";
import FormInputText from "../../components/form/FormInputText";
import TableColumnBankActionType from "../../components/table/TableColumnBankActionType";
import BankActionsHelpModal from "../../edgeManagement/banksActions/BankActionsHelpModal";
// REDUX
import {
  actionAPI,
  usePostActionMutation,
  usePutActionMutation,
  useDeleteActionMutation,
} from "../../../redux/actions/actionsAPI";
import { actionBankAPI, usePutActionBankMutation } from "../../../redux/actions/actionBankAPI";
import { useLazyGetBankQuery } from "../../../redux/banks/bankAPI";
import { updateOpenModalBankActionsHelp } from "../../../redux/banks/bankSlice";
import { useLazyGetAdapterKeysQuery } from "../../../redux/keys/keysAPI";
// LOCAL HELPERS
import {
  groupActionsByBankAndLift,
  BANK_LEVEL_LIFT_LABEL,
  ALL_CARS_LIFT_LABEL,
  pruneActionCatalogId,
} from "./actionsRowGrouping";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// CONTEXT
import { useWizard } from "../WizardContext";
import { WizardOpGate } from "../WizardStepGate";
// WIZARD UTILS
import { WizardStep } from "../utils/wizardInvalidation";
import { useWizardStepDraft } from "../utils/useWizardStepDraft";
import { describeError } from "../utils/describeError";
// WIZARD COMPONENTS
import WizardExportButton from "../components/WizardExportButton";
import WizardReadOnlyId from "../WizardReadOnlyId";
import WizardScheduledActions from "./WizardScheduledActions";
// SCHEDULED ACTIONS
import { ControlOption as ScheduledControlOption } from "../../../components/scheduledActions/scheduledActionTypes";
// FLATBUFFER GENERATED ENUMS
import { Control } from "../../../flatbuffers/generated/control/control";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import {
  ACTION_TYPE_CONFIG,
  ActionButtonTypeName,
  bindsPairedCommand,
  rendersSecondButton,
} from "../../monitoringManagement/Actions/actionTypeConfig";
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import { ActionScope } from "../../../flatbuffers/generated/archive/action-scope";
import { ActionButtonType } from "../../../flatbuffers/generated/archive/action-button-type";
import { valuedMessageTToCompositeKey } from "../../../flatbuffers/types/valuedMessageBuilder";
// FLATBUFFER DISPLAY + GENERATED TYPES
import { formatUuidT } from "../../../flatbuffers/display";
// SCHEMA-DRIVEN MESSAGES — resolves validStates/toggleValidStates (Message
// ids from SelectMessagesMultiple) to human-readable labels for the table.
import { ALL_SCHEMA_MESSAGES } from "../../../customHooks/useSchemaMessages";
import {
  formatCamelCaseLabel,
  UNBOUND_ACTION_REASON,
  UNBOUND_COMMAND_REASON,
} from "../../monitoringManagement/actionButtons/actionButtonUtils";
import { adapterDeclaredMessageIds } from "./adapterMessageScope";
// ActionBankT is the generated Object-API class from the
// flattened-valued-messages flatbuffer_schemas branch — camelCase fields
// are the canonical shape, so we type the lookup map and form-pre-fill
// flow against it directly instead of any-typing.
import { ActionBankT } from "../../../flatbuffers/generated/archive/action-bank";
// LOCAL — pure helpers
import { validateHexColor } from "../utils/colorValidation";
// MODBUS CDO — control commands for a MODBUS adapter live in the binary CDO
// template (fetched from /adapters/:id/cdo), not in the keys table. These
// helpers decode that template so its control writes surface in the actions UI.
import { fetchFlatBufferBinary } from "../../../utils/flatbufferApi";
import { unpackModbusTemplate } from "../../../utils/modbusFlatBuffer";
import {
  materializationWarning,
  readMaterializationStatus,
  willRender,
} from "./actionMaterialization";
import { exposedCommandNames, ORPHAN_TOOLTIP, orphanedActionIds } from "./actionOrphans";
// Binary-CDO control extraction for MODBUS / OPCUA / LOBBYVISION2 / SEQUENT —
// each keeps its control commands only in its protocol-specific binary CDO
// (no `keys` rows). This dispatches to the right decoder by protocol.
import { fetchBinaryCdoControlEntries } from "./binaryCdoControlSources";
// STORE
import { RootState } from "../../../app/store";
import { useAppDispatch } from "../../../app/hooks";

// ************************************************ */
// TYPES ****************************************** */
interface ControlOption {
  label: string;
  value: string;
  numeric: number;
}

/**
 * Runtime guard for a ControlOption arriving from outside the module —
 * a persisted wizard draft, a POST/PUT echo, or a hydrated
 * action_catalog record. Everything on those paths is `any`, and a
 * half-populated object would put the derived fallback in some of the
 * three Command Label/Value/Numeric columns and the stored value in the
 * others; requiring all three fields keeps the trio all-or-nothing.
 */
function isControlOption(value: unknown): value is ControlOption {
  if (!value || typeof value !== "object") return false;
  const opt = value as Partial<ControlOption>;
  return (
    typeof opt.label === "string" &&
    typeof opt.value === "string" &&
    typeof opt.numeric === "number"
  );
}

interface ActionRow {
  key: string;
  id: string;
  label: string;
  messageType: string;
  // Both bankId AND bankName are tracked so edit/delete handlers can
  // resolve the canonical bank (id) without a fragile name lookup, while
  // the grouped renderer can still show the display name. Same pattern
  // for liftId/liftName. Without bankId we'd risk re-parenting an action
  // to the wrong bank when two banks share a display name.
  bankId: string;
  bankName: string;
  liftId: string;
  liftName: string;
  /** Archive.ActionScope as the server derived it; absent on older rows. */
  actionScope?: number;
  targetPosition: number;
  buttonType: string;
  // Preserved so the Edit Modal can pre-fill these conditional fields
  // (TOGGLETIMER → timer + toggle_timer; TOGGLE → toggle_cmd +
  // toggle_label). Without them, opening Edit on a TOGGLE/TOGGLETIMER
  // row and immediately pressing Save would silently strip the
  // conditional state from the server record.
  timer?: number;
  toggleTimer?: number;
  toggleMessageType?: string;
  toggleLabel?: string;
  // Remaining fields the manual-add POST body carries — kept optional
  // since bulk-add paths and older hydrated rows don't always populate
  // every one of them. Surfaced read-only in the Actions table so the
  // operator can see the full action_catalog detail without opening
  // Edit/Visual. Numeric *MessageValue fields mirror message_value /
  // toggle_message_value on the wire (the resolved Control enum value).
  orgId?: string;
  liftType?: string;
  tooltip?: string;
  toggleTooltip?: string;
  color?: string;
  toggleColor?: string;
  validStates?: string[];
  toggleValidStates?: string[];
  messageValue?: number;
  toggleMessageValue?: number;
  // The FULL dropdown option behind messageType / toggleMessageType —
  // {label, value, numeric} — not just its `value` half. Travels to the
  // backend as `command` / `toggle_command` and back, and is what the
  // Command Label / Command Value / Command Numeric table columns read.
  command?: ControlOption;
  toggleCommand?: ControlOption;
}

/**
 * User-facing button-behavior options on the wizard.
 *
 *  - "SINGLE":               plain single click — push fires the assigned
 *                            command once and nothing else. No timer, no
 *                            Toggle Action. Wire value is ActionButtonType
 *                            SWITCH (the backend has no separate "single"
 *                            enum member).
 *  - "SWITCH":               legacy single-click preset — same wire value
 *                            as SINGLE (ActionButtonType.SWITCH), but
 *                            surfaces the optional off command + delay to
 *                            fire a timed off sequence with a cancel
 *                            button. SINGLE and SWITCH are two UI presets
 *                            over the one backend enum value; which preset
 *                            a stored row reopens as is inferred from
 *                            whether it has a toggle command bound (see
 *                            wizardButtonTypeFromString).
 *  - "TOGGLE":               stateful on/off pair (primary + paired off
 *                            command).
 *  - "TOGGLETIMER":          two-button toggle where each press locks both
 *                            buttons for its own countdown (`timer` /
 *                            `toggle_timer`).
 *  - "HOLDBACK":             primary on click; operator must hold the
 *                            confirm button for `timer` seconds before
 *                            the off (toggle) command fires.
 *  - "TURNBACK":             primary on click; after `timer` seconds the
 *                            off (toggle) command auto-fires (unattended).
 *  - "ENERGIZER_TURN_BACK":  specialised TURNBACK used for energizer
 *                            sequences — same shape as TURNBACK, distinct
 *                            wire enum so VTS routes it differently. Not
 *                            offered in WIZARD_BUTTON_TYPE_OPTIONS (no
 *                            longer a selectable preset here), but the
 *                            type/logic stays so existing rows using it
 *                            still render and partially round-trip.
 *
 *  - "ON_OFF":               one command, two buttons — on presses it with
 *                            `active: true`, off with `active: false`. The
 *                            latching shape: a mode or lock that holds until
 *                            it is cleared. What the seeder writes for every
 *                            Control command that is not a call registration.
 *  - "ONE_SHOT":             one command, one button — the CONTROLLER decides
 *                            the off. What the seeder writes for the three
 *                            call registrations (Energize{Car,Up,Down}Call).
 *
 * SINGLE and SWITCH are the only two-to-one mapping; every other
 * WizardButtonType maps 1:1 to an ActionButtonType enum value via
 * wizardButtonTypeToString.
 *
 * Every member offered in WIZARD_BUTTON_TYPE_OPTIONS must have its own case in
 * wizardButtonTypeToString AND its own entry in requestSerializers'
 * toActionButtonType. ON_OFF and ONE_SHOT were added to this union and to the
 * options list without either, so both fell through to the `default` and were
 * silently saved as SWITCH, then coerced again to TOGGLE on the wire — the
 * operator picked "On / Off", the row came back TOGGLE, and editing it again
 * could not repair it. buttonTypeRoundTrip.test.ts now asserts the round trip
 * for every offered option so a third value cannot repeat it.
 */
type WizardButtonType =
  | "SINGLE"
  | "SWITCH"
  | "TOGGLE"
  | "TOGGLETIMER"
  | "HOLDBACK"
  | "TURNBACK"
  | "ENERGIZER_TURN_BACK"
  | "ON_OFF"
  | "ONE_SHOT";

// Selectable presets for the manual-add/edit Button Type dropdown, in the
// order operators should see them. Energizer Turnback and Single Click are
// intentionally absent — both are still valid WizardButtonType values (existing
// rows keep rendering and round-tripping, and SINGLE remains the fallback for a
// stored type the wizard does not surface), they are just no longer offered as a
// choice. Toggle leads the list and is the form's default.
export const WIZARD_BUTTON_TYPE_OPTIONS: {
  value: WizardButtonType;
  label: string;
  description: string;
}[] = [
  {
    value: "TOGGLE",
    label: "Toggle",
    description: "Two buttons: press to activate, press the paired off-button to deactivate.",
  },
  {
    value: "ON_OFF",
    label: "On / Off",
    description:
      "Two buttons that send the SAME command — on energises it, off de-energises it. Only one command is bound, so there is no paired command to leave unset. This is what auto-seeded Control bindings are.",
  },
  {
    value: "ONE_SHOT",
    label: "One-Shot",
    description:
      "One button that pulses the command: it energises on press and de-energises itself after Timer seconds. For controls the CONTROLLER clears, such as registering a hall or car call.",
  },
  {
    value: "HOLDBACK",
    label: "Holdback",
    description:
      "Press to activate. Operator must then hold the confirm button for the timer seconds before the off command fires.",
  },
  {
    value: "TURNBACK",
    label: "Turnback",
    description:
      "Automatic timed turnback: press to activate, the off command auto-fires unattended after the timer seconds elapse.",
  },
  {
    value: "TOGGLETIMER",
    label: "Toggle Timer",
    description:
      "Two buttons (on + off), each press locks both buttons for its own countdown — on uses Timer, off uses Toggle Timer.",
  },
  {
    value: "SWITCH",
    label: "Switch",
    description:
      "Single click with an optional timed off sequence: bind an off command + delay to auto-fire it after the press, with a cancel button.",
  },
];

/**
 * One-line explanation per column, shown on hover over the header.
 *
 * Several of these columns are legitimately empty for a given row, and an
 * operator cannot tell the three apart by looking: a value the server never
 * sends, a value the seeder deliberately leaves unset, and a value that does
 * not apply to this button type used to render alike, as an em dash. They no
 * longer do: a cell resolves to a value, to the effective default, or to an
 * "n/a" that names the reason. The header still explains what the column asks,
 * and each cell explains its own answer.
 */
const COLUMN_HELP: Record<string, string> = {
  "Bank ID":
    "The bank this action belongs to. Actions are bound per bank, so the same command on two banks is two rows.",
  "Org ID": "The organization that owns this action row.",
  "Lift Type":
    "The kind of unit the bound command addresses (ELEVATOR, ESCALATOR, and so on), taken from the command's catalog entry.",
  Lift: "Which car this action addresses. Bank-Level means the command is bank-wide — it has no single car to send to.",
  "Button Type":
    "The interaction the monitoring panel renders for this row: how many buttons appear and what each one sends. Hover the tag itself for what this specific type does.",
  Command: "The vendor command this row sends, by name.",
  "Command Label": "The human-readable name shown for the command in the monitoring panel.",
  "Command Value": "The command's value within its message category.",
  "Command Numeric": "The command's numeric enum ordinal — what actually goes on the wire.",
  "Message Value": "The enum id of the message row this action is bound to.",
  Label: "The text shown on the primary button. Operator-editable.",
  Color:
    "The primary button's colour. With none chosen the cell shows the theme default, which is what the panel renders.",
  "Valid States":
    "Service states in which the primary button is BLOCKED. With none listed the cell reads Never blocked, and the button is offered in every state.",
  "Toggle Command":
    "The paired command sent by the second button. Reads n/a on single-command types, which have no second command to bind.",
  "Toggle Message Value":
    "The enum id of the paired command's message row. Reads n/a on single-command types.",
  "Toggle Label":
    "The text shown on the second button. Reads n/a on the types that render only one.",
  "Toggle Color":
    "The second button's colour, or the theme default the panel falls back to when none is chosen.",
  "Toggle Valid States":
    "Service states in which the second button is BLOCKED. Never blocked means it is offered in every state.",
  "Timer (s)":
    "Seconds this row's timed behaviour uses — a hold, a countdown, or the delay before an off command fires. Reads n/a on the types that run nothing on a clock, and Controller clears on One-Shot, whose latch the controller releases.",
  "Toggle Timer (s)":
    "Seconds the second button's own countdown runs for. Only Toggle Timer has one, so every other type reads n/a.",
};

/**
 * A column header with its explanation attached.
 *
 * Falls through to plain text for a title with no entry, so adding a column
 * cannot break the header — it just goes unexplained, which is visible in
 * review rather than at runtime.
 */
function columnTitle(text: string): React.ReactNode {
  const help = COLUMN_HELP[text];
  if (!help) return text;

  return (
    <Tooltip title={help}>
      <span className="wizard__columnHelp">{text}</span>
    </Tooltip>
  );
}

/**
 * Fixed palette for the action / toggle-action "Action Color" selects.
 * Maps to BankActionInterface.color / toggleColor (hex string on the wire).
 */
const ACTION_COLOR_RED = "#ff4d4f";
const ACTION_COLOR_GREEN = "#38ac68";

const ACTION_COLOR_OPTIONS: { value: string; label: string }[] = [
  { value: ACTION_COLOR_RED, label: "Red" },
  { value: ACTION_COLOR_GREEN, label: "Green" },
  { value: "#faad14", label: "Yellow" },
  { value: "#fa8c16", label: "Orange" },
  { value: "#1890ff", label: "Blue" },
  { value: "#722ed1", label: "Purple" },
  { value: "#13c2c2", label: "Cyan" },
  { value: "#8c8c8c", label: "Gray" },
];

/** Small colored circle used by every Select option that carries a color
 * (Action Color, Toggle Action Color, and now Button Type) — factored
 * out so the three option lists render the exact same dot. */
function ColorDot({ color }: { color: string }): React.ReactElement {
  return (
    <span
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: color,
        marginRight: 8,
      }}
    />
  );
}

/**
 * Fixed color per Button Type, purely a visual aid on the dropdown
 * (matches the same dot treatment as Action Color) — not the wire value,
 * and unrelated to ACTION_COLOR_OPTIONS/bankAction.color.
 */
const BUTTON_TYPE_DOT_COLOR: Partial<Record<WizardButtonType, string>> = {
  SINGLE: "#8c8c8c",
  TOGGLE: "#38ac68",
  HOLDBACK: "#faad14",
  TURNBACK: "#fa8c16",
  TOGGLETIMER: "#1890ff",
  SWITCH: "#722ed1",
};

/**
 * Consolidated shape for the "Add Manual Action" form — one object
 * instead of ~10 parallel useState calls. Field names mirror
 * BankActionInterface (color/timer/label/toggleColor/toggleTimer/
 * toggleLabel/buttonType) so the mapping to the wire payload in
 * handleAddAction stays obvious; messageType/toggleMessageType/
 * tooltip/toggleTooltip are UI-only additions BankActionInterface
 * doesn't carry (command name + operator hint text).
 */
interface ManualActionFormState {
  label: string;
  messageType: string | undefined;
  tooltip: string;
  toggleTooltip: string;
  color: string | undefined;
  toggleColor: string | undefined;
  buttonType: WizardButtonType;
  timer: number;
  toggleTimer: number;
  toggleMessageType: string | undefined;
  toggleLabel: string;
  // Message ids (SelectMessagesMultiple) the runtime checks against the
  // elevator's current service mode to auto-disable the button — see
  // BankActionInterface.validStates / toggleValidStates. Same semantics
  // as the dedicated Bank Actions screen; may be empty.
  validStates: string[];
  toggleValidStates: string[];
  // Messages whose arrival confirms the command was acted on — the wizard-level
  // default for ActionBank.expected_response. Same id convention as validStates
  // (catalog ids, "CONTROL:21" style) because the same picker writes both.
  // EMPTY is meaningful: the action declares no expected response, and the
  // monitoring panel must then report a press as "sent, unverifiable" rather
  // than as confirmed.
  expectedResponse: string[];
  // Verification window in SECONDS. 0 means UNSET, per the schema — not "no
  // timeout" and not "verify immediately" — so the consumer applies its own
  // default. Only meaningful when expectedResponse is non-empty.
  expectedResponseTimeout: number;
  // The whole option object the Control Command / Toggle Off Command
  // dropdowns expose ({label, value, numeric}). messageType /
  // toggleMessageType keep only the `value` half, which is enough to
  // send `cmd`/`toggle_cmd` but loses the operator-facing label and the
  // numeric enum; these carry all three so the POST can ship them as
  // `command` / `toggle_command` without re-deriving anything.
  command: ControlOption | undefined;
  toggleCommand: ControlOption | undefined;
}

const BLANK_MANUAL_ACTION_FORM: ManualActionFormState = {
  label: "",
  messageType: undefined,
  tooltip: "",
  toggleTooltip: "",
  // Green on, red off — the pairing operators expect for an on/off button.
  // Both remain editable; these are only the starting values.
  color: ACTION_COLOR_GREEN,
  toggleColor: ACTION_COLOR_RED,
  buttonType: "TOGGLE",
  timer: 0,
  toggleTimer: 0,
  toggleMessageType: undefined,
  toggleLabel: "",
  validStates: [],
  toggleValidStates: [],
  expectedResponse: [],
  expectedResponseTimeout: 0,
  command: undefined,
  toggleCommand: undefined,
};

function isWizardButtonType(value: string): value is WizardButtonType {
  return (
    value === "SINGLE" ||
    value === "SWITCH" ||
    value === "TOGGLE" ||
    value === "TOGGLETIMER" ||
    value === "HOLDBACK" ||
    value === "TURNBACK" ||
    value === "ENERGIZER_TURN_BACK"
  );
}

/**
 * Map a wizard button-type choice to its enum-name string. The ActionBank
 * payload the backend expects uses the enum NAME (e.g. "SWITCH"), not
 * the numeric value — see BankActionInterface.buttonType in
 * src/interfaces/Bank.interface.ts and BLANK_BANK_ACTION in
 * src/constants/bank.ts. We pull from ActionButtonType so a schema
 * rename propagates at compile time. SINGLE and SWITCH are two UI
 * presets over the same ActionButtonType.SWITCH wire value — see the
 * WizardButtonType doc comment above.
 */
/**
 * The inverse of wizardButtonTypeToString: which preset the Edit modal should
 * show for a row already stored as `value`.
 *
 * Every ActionButtonType name is recognised. It used to know only TOGGLE,
 * TOGGLETIMER and SWITCH and return "SINGLE" for everything else — which is
 * how stored rows were corrupted, and why fixing only the WRITE side would not
 * have been enough. Opening an ON_OFF row showed "Single Click", and saving
 * that back wrote SWITCH: the operator changed nothing and the row was
 * downgraded anyway, on a modal they may have opened only to rename a button.
 *
 * SWITCH still requires a toggle command. Both SINGLE and SWITCH are presets
 * over the same wire value, and the toggle command is what distinguishes them,
 * so a SWITCH row with no toggle really is the SINGLE preset.
 */
export function wizardButtonTypeFromString(
  value: string,
  hasToggleCommand: boolean,
): WizardButtonType {
  if (value === ActionButtonType[ActionButtonType.TOGGLE]) return "TOGGLE";
  if (value === ActionButtonType[ActionButtonType.TOGGLETIMER]) return "TOGGLETIMER";
  if (value === ActionButtonType[ActionButtonType.ON_OFF]) return "ON_OFF";
  if (value === ActionButtonType[ActionButtonType.ONE_SHOT]) return "ONE_SHOT";
  if (value === ActionButtonType[ActionButtonType.HOLDBACK]) return "HOLDBACK";
  if (value === ActionButtonType[ActionButtonType.TURNBACK]) return "TURNBACK";
  if (value === ActionButtonType[ActionButtonType.ENERGIZER_TURN_BACK]) {
    return "ENERGIZER_TURN_BACK";
  }
  if (value === ActionButtonType[ActionButtonType.SWITCH] && hasToggleCommand) return "SWITCH";

  return "SINGLE";
}

export function wizardButtonTypeToString(bt: WizardButtonType): string {
  switch (bt) {
    case "TOGGLE":
      return ActionButtonType[ActionButtonType.TOGGLE];
    case "TOGGLETIMER":
      return ActionButtonType[ActionButtonType.TOGGLETIMER];
    case "HOLDBACK":
      return ActionButtonType[ActionButtonType.HOLDBACK];
    case "TURNBACK":
      return ActionButtonType[ActionButtonType.TURNBACK];
    case "ENERGIZER_TURN_BACK":
      return ActionButtonType[ActionButtonType.ENERGIZER_TURN_BACK];
    case "ON_OFF":
      return ActionButtonType[ActionButtonType.ON_OFF];
    case "ONE_SHOT":
      return ActionButtonType[ActionButtonType.ONE_SHOT];
    case "SINGLE":
    case "SWITCH":
      return ActionButtonType[ActionButtonType.SWITCH];
    default:
      return ActionButtonType[ActionButtonType.SWITCH];
  }
}

/**
 * Per-type capability table — the single place a button type's field
 * requirements live. Adding a new type means adding one row here (plus
 * its WIZARD_BUTTON_TYPE_OPTIONS entry), not editing four predicate
 * functions and their call sites.
 *
 *  - needsTimer:       timer is REQUIRED (> 0). TOGGLETIMER counts down
 *                      the primary button's lockout; HOLDBACK / TURNBACK /
 *                      ENERGIZER_TURN_BACK use it as the hold / delay
 *                      before the off command fires.
 *  - needsToggle:      the paired off command is REQUIRED. TOGGLE and
 *                      TOGGLETIMER render it as a second button; the
 *                      turnback family fires it after the timer.
 *                      TOGGLETIMER was previously missing this, which
 *                      produced runtime rows whose second button had no
 *                      command bound.
 *  - optionalToggle:   the paired off command is OPTIONAL. SWITCH fires
 *                      its primary once; binding an off command adds the
 *                      delayed off sequence plus a cancel button.
 *  - optionalTimer:    the timer is OPTIONAL — offered, never required.
 *                      SWITCH uses it as the delay before its off command;
 *                      ONE_SHOT as the pulse width before it deasserts the
 *                      bit itself (@see ActionButtonOneShot's
 *                      `releaseAfterMs`), leaving the controller to own the
 *                      clear when it is unset.
 *
 *                      This is its own axis because it was previously read
 *                      off optionalToggle, which conflated two unrelated
 *                      questions. ONE_SHOT wants an optional TIMER and has
 *                      no paired command at all, so it could not answer
 *                      true without also claiming a second command — and
 *                      answering false disabled the input on the one type
 *                      whose own doc comment promised it was offered. Every
 *                      type's behaviour is unchanged: SWITCH, the only type
 *                      the old expression reached through optionalToggle,
 *                      carries optionalTimer explicitly now.
 *  - needsToggleTimer: the second timer is REQUIRED. Only TOGGLETIMER —
 *                      its off button locks both buttons for
 *                      toggle_timer seconds (timerToggleAction prop).
 */
const BUTTON_TYPE_CAPS: Record<
  WizardButtonType,
  {
    needsTimer: boolean;
    needsToggle: boolean;
    optionalToggle: boolean;
    optionalTimer: boolean;
    needsToggleTimer: boolean;
  }
> = {
  SINGLE: {
    needsTimer: false,
    needsToggle: false,
    optionalToggle: false,
    optionalTimer: false,
    needsToggleTimer: false,
  },
  // One command, two buttons — on sends it active, off sends the SAME command
  // inactive. No paired command and no timer, which is exactly why it exists:
  // it is the shape an auto-seeded Control binding has, and typing those rows
  // TOGGLE (which REQUIRES a paired command) is what left their off button
  // bound to nothing.
  ON_OFF: {
    needsTimer: false,
    needsToggle: false,
    optionalToggle: false,
    optionalTimer: false,
    needsToggleTimer: false,
  },
  // One command, one button, self-clearing. The timer is OPTIONAL, not
  // required: an operator can set a pulse width for a controller that scans
  // slowly, and leaving it unset hands the clear to the controller, which is
  // what a hall or car call wants. `optionalTimer` is what makes that promise
  // true — this comment made it for a while, and the flags did not keep it.
  ONE_SHOT: {
    needsTimer: false,
    needsToggle: false,
    optionalToggle: false,
    // The pulse width, and the reason this axis exists. Optional rather than
    // required because unset is a legitimate and common configuration — the
    // seeder does not write it and the runtime does not need it — so requiring
    // it would mark every auto-seeded row invalid for a field nobody set.
    optionalTimer: true,
    needsToggleTimer: false,
  },
  SWITCH: {
    needsTimer: false,
    needsToggle: false,
    optionalToggle: true,
    // Explicit, where it used to be implied by optionalToggle. SWITCH is the
    // only type the old `needsTimer || supportsOptionalToggle` expression
    // reached this way, so naming it here is what keeps the split behaviourless.
    optionalTimer: true,
    needsToggleTimer: false,
  },
  TOGGLE: {
    needsTimer: false,
    needsToggle: true,
    optionalToggle: false,
    optionalTimer: false,
    needsToggleTimer: false,
  },
  TOGGLETIMER: {
    needsTimer: true,
    needsToggle: true,
    optionalToggle: false,
    optionalTimer: false,
    needsToggleTimer: true,
  },
  HOLDBACK: {
    needsTimer: true,
    needsToggle: true,
    optionalToggle: false,
    optionalTimer: false,
    needsToggleTimer: false,
  },
  TURNBACK: {
    needsTimer: true,
    needsToggle: true,
    optionalToggle: false,
    optionalTimer: false,
    needsToggleTimer: false,
  },
  ENERGIZER_TURN_BACK: {
    needsTimer: true,
    needsToggle: true,
    optionalToggle: false,
    optionalTimer: false,
    needsToggleTimer: false,
  },
};

export function needsTimer(bt: WizardButtonType): boolean {
  return BUTTON_TYPE_CAPS[bt].needsTimer;
}

export function needsToggle(bt: WizardButtonType): boolean {
  return BUTTON_TYPE_CAPS[bt].needsToggle;
}

export function supportsOptionalToggle(bt: WizardButtonType): boolean {
  return BUTTON_TYPE_CAPS[bt].optionalToggle;
}

/** The type OFFERS a timer without requiring one — SWITCH and ONE_SHOT. */
export function supportsOptionalTimer(bt: WizardButtonType): boolean {
  return BUTTON_TYPE_CAPS[bt].optionalTimer;
}

export function needsToggleTimer(bt: WizardButtonType): boolean {
  return BUTTON_TYPE_CAPS[bt].needsToggleTimer;
}

/**
 * Whether the timer input is offered at all — required or optional.
 *
 * Reads the two TIMER flags, where it used to read a timer flag and a TOGGLE
 * one. That conflation is what disabled the input on ONE_SHOT: the type has no
 * paired command, so it could not answer optionalToggle true, and answering
 * false took away the pulse-width field its own capability comment promised.
 */
export function usesTimerField(bt: WizardButtonType): boolean {
  return needsTimer(bt) || supportsOptionalTimer(bt);
}

/** Mandatory or optional toggle types both surface the off-command inputs. */
export function usesToggleField(bt: WizardButtonType): boolean {
  return needsToggle(bt) || supportsOptionalToggle(bt);
}

/**
 * Per-type help text under the Timer input — defined once so the create
 * form and the edit modal can never drift apart.
 */
export function timerHelpText(bt: WizardButtonType): string {
  switch (bt) {
    case "SWITCH":
      return "Optional: with an off command bound, it auto-fires this many seconds after the press (0 = 1s legacy pulse).";
    case "ONE_SHOT":
      return "Optional: how long the command stays energised before this UI deasserts it. Leave 0 to send once and let the controller clear it — what a hall or car call needs.";
    case "TOGGLETIMER":
      return "Pressing the on button locks both buttons for this many seconds.";
    case "HOLDBACK":
      return "Operator must hold the confirm button for this many seconds before the off command fires.";
    case "TURNBACK":
      return "After this many seconds the off command auto-fires unattended.";
    case "ENERGIZER_TURN_BACK":
      return "Energizer pulse duration in seconds before the off command auto-fires.";
    default:
      return "";
  }
}

/** Label for the paired off command — two-button types say "Toggle Off". */
export function toggleCommandLabel(bt: WizardButtonType): string {
  return bt === "TOGGLE" || bt === "TOGGLETIMER" ? "Toggle Off Command" : "Follow-up Command";
}

/** Label for the paired off label input, matching toggleCommandLabel. */
export function toggleLabelLabel(bt: WizardButtonType): string {
  return bt === "TOGGLE" || bt === "TOGGLETIMER" ? "Toggle Off Label" : "Follow-up Label";
}

export type { WizardButtonType };

/**
 * Pick the AntD Tag color for the actions-table Button Type column.
 * Mirrors TableColumnBankActionType so the wizard preview and the
 * dedicated Bank Actions edit screen stay visually aligned.
 */
export function buttonTypeTagColor(value: string): string {
  if (value === ActionButtonType[ActionButtonType.TOGGLE]) return "blue";
  if (value === ActionButtonType[ActionButtonType.TOGGLETIMER]) return "orange";
  if (value === ActionButtonType[ActionButtonType.HOLDBACK]) return "purple";
  if (value === ActionButtonType[ActionButtonType.TURNBACK]) return "geekblue";
  if (value === ActionButtonType[ActionButtonType.ENERGIZER_TURN_BACK]) return "volcano";
  if (value === ActionButtonType[ActionButtonType.SWITCH]) return "magenta";
  return "default";
}

/**
 * Resolve validStates/toggleValidStates (Message ids, e.g. "SERVICE:3")
 * back to their human-readable short message for the Actions table —
 * mirrors SelectMessagesMultiple's own read-only rendering so the wizard
 * and the dedicated Bank Actions screen describe the same ids the same
 * way.
 */
function describeMessageIds(ids: string[]): string {
  return ids
    .map((id) => {
      const msg = ALL_SCHEMA_MESSAGES.find((m) => m.id === id);
      return msg ? `${msg.message_type?.name || ""}: ${msg.shortMessage}` : id;
    })
    .join(", ");
}

/** Small swatch + hex text for the Color/Toggle Color table columns. */
function renderColorSwatch(color: string): React.ReactNode {
  return (
    <Space size="small">
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: color,
        }}
      />
      {color}
    </Space>
  );
}

/** Normalizes an ActionBankT text field (typed `string | Uint8Array |
 * null` per the generated Object API) down to `string | undefined`, the
 * shape every table-column fallback below expects. */
function abTextField(val: string | Uint8Array | null | undefined): string | undefined {
  return typeof val === "string" && val ? val : undefined;
}

/**
 * The `/action-banks/by-bank/:bankId` (and /by-organization) endpoints
 * are typed as FlatBuffer-decoded `ActionBankT` (camelCase), but
 * `flatBufferBaseQuery` falls back to plain JSON whenever the server
 * responds without an `application/flatbuffers` Content-Type — which,
 * for this endpoint, is a snake_case object with nested `action`/`bank`/
 * `message` relations (`action_id`, `toggle_color`, `toggle_timer`,
 * `toggle_valid_states`, …), not an ActionBankT instance at all. Every
 * field read off an `ab` below has to tolerate both shapes, starting
 * with the key itself: without this, `ab.actionId` is `undefined` on
 * the JSON shape, `actionBankMap` never gets a single entry, and every
 * table cell that falls back to it reports the field unset no matter what
 * the raw response actually contains.
 */
function resolveActionBankActionId(ab: any): string {
  return formatUuidT(ab?.actionId) || String(ab?.action_id ?? ab?.action?.id ?? "");
}

/** Dual-read a snake_case-only field action_bank's JSON fallback shape
 * carries under a different name than the ActionBankT camelCase getter
 * (toggle_color/toggle_timer/toggle_valid_states — color/timer/valid_states
 * keep the same name in both shapes, so they don't need this). */
function abFallbackField<T>(ab: any, camelKey: string, snakeKey: string): T | undefined {
  const val = ab?.[camelKey] ?? ab?.[snakeKey];
  return val ?? undefined;
}

/** Compact copyable id for the Bank ID/Org ID table columns — a full
 * WizardReadOnlyId (label + input + copy button) is too heavy for a
 * table cell. */
function renderCopyableId(id: string | undefined, missingReason: string): React.ReactNode {
  if (!id) return renderUnconfigured(missingReason);
  return (
    <Typography.Text copyable={{ text: id }} style={{ fontSize: 12, whiteSpace: "nowrap" }}>
      {`${id.slice(0, 8)}\u2026`}
    </Typography.Text>
  );
}

/**
 * Returns true when the adapter object represents an MCE protocol adapter.
 * Accepts both camelCase (FlatBuffer/API) and snake_case (legacy JSON) field names,
 * and both numeric (AdapterProtocol.MCE === 0) and string ("MCE") values.
 */
export function isMceAdapter(adapter: any): boolean {
  if (adapter == null) return false;
  const pt = adapter.protocolType ?? adapter.protocol_type;
  if (pt === AdapterProtocol.MCE) return true;
  if (typeof pt === "string" && pt.toUpperCase() === "MCE") return true;
  return false;
}

/**
 * Returns true when the adapter object represents a MODBUS protocol adapter.
 * Accepts both camelCase (FlatBuffer/API) and snake_case (legacy JSON) field
 * names, and both numeric (AdapterProtocol.MODBUS === 1) and string ("MODBUS")
 * values. Used to give MODBUS banks the same manual-add escape hatch MCE has
 * when the adapter's binary CDO decodes to zero control writes.
 */
export function isModbusAdapter(adapter: any): boolean {
  if (adapter == null) return false;
  const pt = adapter.protocolType ?? adapter.protocol_type;
  if (pt === AdapterProtocol.MODBUS) return true;
  if (typeof pt === "string" && pt.toUpperCase() === "MODBUS") return true;
  return false;
}

/**
 * Resolve a bank's uppercase protocol name for the binary-CDO control
 * fetch. Prefers the wizard's per-bank protocol map (MODBUS/OPCUA/LV2/SEQUENT
 * banks arrive with no nested `adapter` on the bank response), then falls
 * back to the adapter object's protocolType (numeric enum or string).
 */
export function resolveProtocolKey(protocolFromMap: string | undefined, adapter: any): string {
  const mapped = (protocolFromMap ?? "").toUpperCase();
  if (mapped) return mapped;
  const pt = adapter?.protocolType ?? adapter?.protocol_type;
  if (typeof pt === "number") return (AdapterProtocol[pt] ?? "").toUpperCase();
  if (typeof pt === "string") return pt.toUpperCase();
  return "";
}

// ************************************************ */
// HELPERS **************************************** */
const CONTROL_NAME_BY_VALUE = new Map<number, string>(
  Object.entries(Control as unknown as Record<string, string | number>)
    .filter(([, v]) => typeof v === "number")
    .map(([key, v]) => [v as number, key]),
);

function enumToControlOptions(enumObj: Record<string, string | number>): ControlOption[] {
  return Object.entries(enumObj)
    .filter(([, v]) => typeof v === "number")
    .map(([key, v]) => ({
      label: `Control::${key}`,
      value: key,
      numeric: v as number,
    }));
}

/**
 * Turn a raw Control command name (e.g. "DownHallCallUnlock", the
 * ControlOption.value operators pick from the dropdown) into a
 * human-readable phrase (e.g. "Down Hall Call Unlock") for pre-filling
 * Label/Tooltip when a command is selected. Splits camelCase/PascalCase
 * boundaries — including consecutive-caps acronyms — and normalizes
 * underscores to spaces.
 */
function humanizeControlCommandName(raw: string): string {
  return raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
}

/**
 * TOGGLE is a single physical button with two faces: the same command drives
 * both, one turning it on and one turning it off. The operator therefore never
 * picks the off command for it — it is always the on command — so the wizard
 * mirrors the selection instead of asking twice, and the Toggle Command select
 * is disabled for this type.
 *
 * Other toggle-capable types (HOLDBACK, TURNBACK, TOGGLETIMER, SWITCH's
 * off-sequence) bind a genuinely DIFFERENT off command, so they keep their own
 * editable select.
 */
export function mirrorsToggleCommand(bt: WizardButtonType): boolean {
  return bt === "TOGGLE";
}

/**
 * Auto-filled labels for a command, e.g. "Open Door ON" / "Open Door OFF".
 *
 * The ON/OFF suffix only makes sense when the button actually has two faces,
 * so a type with no toggle counterpart keeps the bare command name. Operators
 * can still edit either field afterwards — these are starting values.
 */
export function autoActionLabels(
  cmd: string,
  bt: WizardButtonType,
): { label: string; toggleLabel: string } {
  const humanized = humanizeControlCommandName(cmd);
  if (!usesToggleField(bt)) return { label: humanized, toggleLabel: "" };
  return { label: `${humanized} ON`, toggleLabel: `${humanized} OFF` };
}

const ALL_CONTROL_OPTIONS = enumToControlOptions(
  Control as unknown as Record<string, string | number>,
);

/**
 * Resolve the options for the manual "Control Command" dropdown.
 *
 * The dropdown is the ONLY way an operator adds an action by hand, so an
 * empty option list hard-blocks the step. Precedence:
 *
 *  1. MCE adapters expose no per-key/CDO control mappings (they map
 *     internally in VTS), so they always get the full Control catalog.
 *  2. Adapter-derived options (LN1 keys, MODBUS CDO writes, offset maps)
 *     are used verbatim whenever the adapter yielded at least one.
 *  3. MODBUS adapters whose binary CDO decoded to ZERO control writes — or
 *     whose `/adapters/:id/cdo` fetch failed (both collapse to an empty
 *     list, since `fetchModbusCdoControlEntries` swallows the difference) —
 *     would otherwise leave the operator unable to add ANY action. Fall
 *     back to the full catalog for them too, matching the MCE escape hatch,
 *     so setup is never hard-blocked. `modbusCatalogFallbackActive` drives a
 *     warning so the operator knows these are catalog commands rather than
 *     adapter-verified writes.
 *
 * Split into a pure helper (no nested ternary) so the create form and the
 * unit tests share one definition.
 */
export function resolveManualCommandOptions(params: {
  isMceBank: boolean;
  isModbusBank: boolean;
  adapterControlOptions: ControlOption[] | undefined;
  showAllCommands?: boolean;
}): ControlOption[] {
  const { isMceBank, isModbusBank, adapterControlOptions, showAllCommands = false } = params;
  // 0. Operator escape hatch. The Actions step is the sole source of truth for
  //    which control buttons the monitoring panel renders, so a command the
  //    adapter does not advertise is a command the site can no longer express at
  //    all. Some adapters under-report (a keyed adapter whose secure-call keys
  //    were never imported, a controller that accepts a command it does not list),
  //    and before this the operator had no way to add one. Opt-in, never the
  //    default, so the adapter-verified list stays the normal path.
  if (showAllCommands) return ALL_CONTROL_OPTIONS;
  if (isMceBank) return ALL_CONTROL_OPTIONS;
  if (adapterControlOptions && adapterControlOptions.length > 0) return adapterControlOptions;
  if (isModbusBank) return ALL_CONTROL_OPTIONS;
  return adapterControlOptions ?? [];
}

/**
 * True when the operator has opted into the full Control catalog on a bank whose
 * adapter DID advertise commands — i.e. the list on screen now includes commands
 * the adapter never verified. Drives the same style of warning the MODBUS
 * fallback uses, so "adapter-verified" and "catalog" are never confused.
 */
export function isManualCatalogOverrideActive(params: {
  isMceBank: boolean;
  isModbusBank: boolean;
  adapterControlOptions: ControlOption[] | undefined;
  showAllCommands: boolean;
}): boolean {
  const { isMceBank, isModbusBank, adapterControlOptions, showAllCommands } = params;
  if (!showAllCommands) return false;
  if (isMceBank || isModbusBank) return false;
  return (adapterControlOptions?.length ?? 0) > 0;
}

/**
 * True when the manual dropdown is showing the full catalog *because* a
 * MODBUS adapter yielded no decodable control writes — i.e. the fallback in
 * `resolveManualCommandOptions` case 3 is active. Drives the operator-facing
 * warning. `adapterControlOptions === undefined` means "not fetched yet"
 * (loading), which must NOT trigger the warning.
 */
export function isModbusCatalogFallbackActive(params: {
  isMceBank: boolean;
  isModbusBank: boolean;
  adapterControlOptions: ControlOption[] | undefined;
}): boolean {
  const { isMceBank, isModbusBank, adapterControlOptions } = params;
  if (isMceBank || !isModbusBank) return false;
  return adapterControlOptions !== undefined && adapterControlOptions.length === 0;
}

/**
 * Resolve a command string back to the FULL dropdown option the
 * operator picked ({label, value, numeric}). This whole object is what
 * rides to the backend as `command` / `toggle_command`, so the server
 * keeps the operator-facing label and the raw enum name next to the
 * numeric value instead of having to re-derive them. Single definition
 * shared by the create form (commandOptions) and the edit modal
 * (editCommandOptions).
 */
export function resolveControlOptionForCommand(
  options: ControlOption[],
  cmd: string | undefined,
): ControlOption | undefined {
  return cmd ? options.find((option) => option.value === cmd) : undefined;
}

/**
 * Resolve a command string to its Control enum value from an option
 * list. Same lookup as resolveControlOptionForCommand, narrowed to the
 * numeric — kept as its own name because `message_value` /
 * `toggle_message_value` are numeric-only wire fields.
 */
export function resolveNumericForCommand(
  options: ControlOption[],
  cmd: string | undefined,
): number | undefined {
  return resolveControlOptionForCommand(options, cmd)?.numeric;
}

/**
 * Rebuild the {label, value, numeric} option from the plain `cmd` /
 * `message_value` pair, which DOES travel on every transport.
 *
 * The backend persists `command` / `toggle_command`, but it can only
 * SERVE them on the JSON batch-create response: the binary FlatBuffer
 * schema was never extended, and flatBufferBaseQuery sends
 * `Accept: application/flatbuffers` on every call, so the simple POST,
 * the PUT and every GET come back without them. Deriving here is what
 * keeps the three Command columns populated after a reload — and it
 * fills legacy rows too, whose stored columns are NULL.
 *
 * `numeric` prefers the record's own message_value and only falls back
 * to the generated Control enum when the record carries none, so a row
 * whose stored value disagrees with the enum stays visible next to the
 * Message Value column instead of being papered over. The check is
 * `typeof === "number"`, never truthiness: Control.CarCallLock is a
 * legitimate 0.
 */
/**
 * Does this row's button type have a second command at all?
 *
 * Answered from the row's stored button type through the SAME predicate the
 * create form and edit modal use, so the table cannot disagree with them about
 * what a type is.
 */
function rowActionType(row: { buttonType?: string }): ActionButtonTypeName | undefined {
  const name = row?.buttonType;
  return typeof name === "string" && name in ACTION_TYPE_CONFIG
    ? (name as ActionButtonTypeName)
    : undefined;
}

/**
 * "n/a" for a toggle cell on a row whose type has no second command.
 *
 * An em dash means "nothing configured here", which invites an operator to go
 * configure it. On a SINGLE or ONE_SHOT row there is nothing to configure —
 * the type binds one command — so every toggle column read as six unexplained
 * gaps that could not be closed. Saying so distinguishes "not set" from "does
 * not apply", which is the whole reason these cells were being read.
 */
export const NO_PAIRED_COMMAND_REASON =
  "This button type binds a single command, so there is no paired command to configure.";

export const NO_SECOND_BUTTON_REASON =
  "This button type renders a single button, so it has no second button to label or colour.";

export function renderNotApplicable(reason: string): React.ReactNode {
  return (
    <Tooltip title={reason}>
      <span className="stepActions__notApplicable">n/a</span>
    </Tooltip>
  );
}

/**
 * "n/a" for a cell whose field DOES exist on this row and carries no value.
 *
 * Deliberately a different glyph from renderNotApplicable, because they answer
 * different questions. The italic one says "this column asks something your
 * button type has no answer to" — a property of the type, needing no action.
 * This one says "this column asks something this row is supposed to answer and
 * does not", which an operator has to go and fix: a row with no command bound
 * renders a button that sends nothing.
 *
 * Rendering both as an em dash is what made the table unreadable. The operator
 * could not separate the cells that are blank by design from the one cell that
 * means the action is broken.
 */
export function renderUnconfigured(reason: string): React.ReactNode {
  return (
    <Tooltip title={reason}>
      <Tag color="orange" className="stepActions__unconfigured">
        n/a
      </Tag>
    </Tooltip>
  );
}

/**
 * The EFFECTIVE value of a field the operator left unset.
 *
 * The third state, and by far the most common one. Colour, valid states and the
 * one-shot release are optional fields with a DEFINED runtime fallback, so
 * "unset" is not missing information: the answer is knowable, it just is not
 * stored on the row. Printing that answer is what the column was asked for, and
 * the tooltip names where it came from so nobody reads it as a stored value.
 *
 * Every string these cells show is taken from what the monitoring runtime
 * actually does with an unset field, not from what the field's absence
 * suggests — see the call sites, each of which cites the code it describes.
 */
export function renderDefaulted(value: React.ReactNode, reason: string): React.ReactNode {
  return (
    <Tooltip title={reason}>
      <span className="stepActions__defaulted">{value}</span>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// CELL REASONS
// One sentence per empty state, saying what is true rather than that something
// is absent. Held together here so two columns describing the same runtime
// fallback cannot drift into describing it differently.
// ---------------------------------------------------------------------------

export const NO_BANK_ID_REASON =
  "The server returned this row without a bank id, so it cannot be traced back to a bank. Re-run the Banks step if it persists.";

export const NO_ORG_ID_REASON =
  "The server returned this row without an organization id, so it cannot be traced back to an organization.";

export const NO_LIFT_TYPE_REASON =
  "The bound command's catalog entry names no unit class, so this row is not restricted to elevators or escalators specifically.";

export const NO_LABEL_REASON =
  "This action has no label, so its button renders unnamed in the monitoring panel. Set one from the Edit action button.";

export const DERIVED_COMMAND_REASON =
  "This row predates the Control Command picker, so it stores no command object. Shown is the bound command's own name.";

/** @see ActionCommandButton — with no colour it renders no filled variant. */
export const DEFAULT_COLOR_REASON =
  "No colour is stored on this action, so the monitoring panel renders its button in the theme's default style instead of a filled colour.";

export const DEFAULT_TOGGLE_COLOR_REASON =
  "No colour is stored for the second button, so the monitoring panel renders it in the theme's default style instead of a filled colour.";

/** @see resolveValidStateEnums / blockedByService — the list is a BLOCK list. */
export const NO_VALID_STATES_REASON =
  "No service states are listed, so this button is never blocked by the car's state — it stays available in every one.";

export const NO_TOGGLE_VALID_STATES_REASON =
  "No service states are listed for the second button, so it is never blocked by the car's state.";

export const NO_TIMED_BEHAVIOUR_REASON =
  "This button type runs nothing on a clock — no hold, no countdown and no delayed follow-up — so it has no duration to configure.";

/** @see ActionButtonOneShot — `releaseAfterMs` is null when timer is unset. */
export const ONE_SHOT_NO_RELEASE_REASON =
  "One-Shot with no timer sends the command once and leaves the controller to clear it, which is what a hall or car call needs: the call latches on the pulse and clears when the car arrives. Set a timer from the Edit action button to have this UI deassert after a fixed pulse instead.";

export const OPTIONAL_TIMER_UNSET_REASON =
  "This type's timer is optional and none is set, so the follow-up command fires on the legacy one-second pulse rather than a configured delay.";

export const REQUIRED_TIMER_UNSET_REASON =
  "This button type needs a duration and none is set, so its timed behaviour cannot run. Set a timer from the Edit action button.";

export const NO_SECOND_TIMER_REASON =
  "Only Toggle Timer gives its second button a countdown of its own, so there is no second duration to configure here.";

export const REQUIRED_TOGGLE_TIMER_UNSET_REASON =
  "Toggle Timer needs a second duration and none is set, so its off button never locks. Set one from the Edit action button.";

export const NO_TOGGLE_LABEL_REASON =
  "This type renders a second button and no label is stored for it, so it renders unnamed. Set one from the Edit action button.";

/**
 * A paired-COMMAND cell (Toggle Command, Toggle Message Value).
 *
 * Not applicable on a type that binds one command — including ON_OFF, whose
 * two buttons both send the primary.
 */
function renderPairedCommandCell(row: ActionRow, render: () => React.ReactNode): React.ReactNode {
  const type = rowActionType(row);
  if (type === undefined || bindsPairedCommand(type)) return render();
  return renderNotApplicable(NO_PAIRED_COMMAND_REASON);
}

/**
 * A second-BUTTON cell (Toggle Label, Colour, Valid States, Timer).
 *
 * Applicable wherever a second button is drawn, which includes ON_OFF and the
 * types whose second button is inert — it still carries a label and a
 * countdown.
 */
function renderSecondButtonCell(row: ActionRow, render: () => React.ReactNode): React.ReactNode {
  const type = rowActionType(row);
  if (type === undefined || rendersSecondButton(type)) return render();
  return renderNotApplicable(NO_SECOND_BUTTON_REASON);
}

/**
 * A duration, or undefined for every value that means "unset".
 *
 * Zero is unset, not a zero-second timer: the schema uses 0 as the absent
 * sentinel (see the `timer` field comment on the manual-add form), which is why
 * the action_bank fallbacks have always required `> 0`. Rendering a literal "0"
 * told the operator a duration was configured when none was.
 */
export function positiveDuration(value: number | undefined): number | undefined {
  return typeof value === "number" && value > 0 ? value : undefined;
}

/**
 * The Timer cell.
 *
 * Four distinct answers, where there used to be one em dash:
 *
 *  - a stored duration;
 *  - ONE_SHOT with none — the controller owns the clear, which is the point of
 *    the type, not an omission (@see ActionButtonOneShot's `releaseAfterMs`);
 *  - a type that REQUIRES one and has none — broken, and flagged as such;
 *  - a type with no timed behaviour at all — genuinely not applicable.
 *
 * ONE_SHOT is tested before the capability flags because its optional timer
 * means something no other type's does. `supportsOptionalTimer` is true for it
 * and for SWITCH, but an unset SWITCH timer falls back to the legacy one-second
 * pulse while an unset ONE_SHOT timer means this UI never deasserts at all —
 * the controller does. One branch cannot say both.
 */
export function renderTimerCell(row: ActionRow, value: number | undefined): React.ReactNode {
  const stored = positiveDuration(value);
  if (stored !== undefined) return stored;

  const type = rowActionType(row);
  // An unrecognised type gets the actionable answer, never the dismissive one:
  // this build cannot claim a field does not apply to a type it does not know.
  if (type === undefined) return renderUnconfigured(REQUIRED_TIMER_UNSET_REASON);
  if (type === "ONE_SHOT") return renderDefaulted("Controller clears", ONE_SHOT_NO_RELEASE_REASON);
  if (needsTimer(type)) return renderUnconfigured(REQUIRED_TIMER_UNSET_REASON);
  if (supportsOptionalTimer(type)) return renderDefaulted("1s pulse", OPTIONAL_TIMER_UNSET_REASON);
  return renderNotApplicable(NO_TIMED_BEHAVIOUR_REASON);
}

/**
 * The Toggle Timer cell — the same three-way split, one type narrower.
 *
 * TOGGLETIMER is the only type whose second button counts down on its own, so
 * every other type's blank here was never a gap to close.
 */
export function renderToggleTimerCell(row: ActionRow, value: number | undefined): React.ReactNode {
  const stored = positiveDuration(value);
  if (stored !== undefined) return stored;

  const type = rowActionType(row);
  if (type === undefined || needsToggleTimer(type))
    return renderUnconfigured(REQUIRED_TOGGLE_TIMER_UNSET_REASON);
  return renderNotApplicable(NO_SECOND_TIMER_REASON);
}

/**
 * A Valid States cell.
 *
 * The list is a BLOCK list — `blockedByService` disables the button when the
 * car's live service state appears in it (@see resolveValidStateEnums) — so an
 * empty one is not an unanswered question. It means the button is offered in
 * every state, which is a fact worth printing rather than a blank worth
 * investigating.
 */
export function renderValidStatesCell(ids: string[] | undefined, reason: string): React.ReactNode {
  if (!ids || ids.length === 0) return renderDefaulted("Never blocked", reason);
  return describeMessageIds(ids);
}

/**
 * A colour cell.
 *
 * With no colour stored, ActionCommandButton applies neither its filled variant
 * class nor an inline background, so the button renders in the theme's own
 * default style. That is the effective value, and it is what this prints —
 * NOT the button type's accent, which tints the card and never the button.
 */
export function renderColorCell(color: string | undefined, reason: string): React.ReactNode {
  if (!color) return renderDefaulted("Theme default", reason);
  return renderColorSwatch(color);
}

/**
 * The unit class named on an ActionBank row itself.
 *
 * `Archive.LiftType` is a numeric enum on the FlatBuffer path and a plain
 * string on the JSON one, and the reverse lookup covers both: indexing the
 * generated enum by an ordinal yields the member name, and indexing it by a
 * name yields a number, which is rejected here. ELEVATOR (0) is the schema
 * DEFAULT, so it is what a row with nothing to say also reads as — a
 * distinction with no consequence, since every consumer treats the absent case
 * as ELEVATOR too.
 */
export function rowLiftTypeName(row: any): string | undefined {
  const raw = row?.liftType ?? row?.lift_type;
  if (typeof raw === "string") return raw || undefined;
  const numeric = rowNumber(raw);
  if (numeric === undefined) return undefined;
  const name = (LiftType as unknown as Record<number, string>)[numeric];
  return typeof name === "string" ? name : undefined;
}

/**
 * An ActionBank row's own bound-command value, or undefined when it carries
 * none.
 *
 * The wire default is -1, chosen because 0 is a real command
 * (Control::CarCallLock) and could not double as "unset". Returning the
 * sentinel as a number would print "-1" in the Message Value column and hand
 * deriveControlOption something to look up.
 */
export function boundCommandValue(raw: unknown): number | undefined {
  const value = rowNumber(raw);
  return typeof value === "number" && value >= 0 ? value : undefined;
}

/**
 * Name a bound command from the row's own value AND the category it came from.
 *
 * Both halves are required, and the second is not a formality: the LN2
 * categories are separate enums that each start at 0, so their values collide —
 * 29 is Traffic::CarToLobby and also Control::DisableDoor. Naming a value
 * without checking its category does not fail, it silently produces a real but
 * DIFFERENT command, which is the exact class of bug that put two inert buttons
 * on Control::RunDown and Control::DisableDoor. A row whose category is
 * anything other than Control is left unnamed rather than guessed at.
 */
export function controlCommandName(value: unknown, contentType: unknown): string | undefined {
  const numeric = typeof value === "number" && value >= 0 ? value : undefined;
  if (numeric === undefined) return undefined;
  if (rowNumber(contentType) !== MessageContent.Control_ControlMessage) return undefined;
  return ALL_CONTROL_OPTIONS.find((option) => option.numeric === numeric)?.value;
}

export function deriveControlOption(
  cmd: unknown,
  messageValue: unknown,
): ControlOption | undefined {
  if (typeof cmd !== "string" || !cmd) return undefined;
  const numeric =
    typeof messageValue === "number"
      ? messageValue
      : resolveControlOptionForCommand(ALL_CONTROL_OPTIONS, cmd)?.numeric;
  if (typeof numeric !== "number") return undefined;
  return { label: `Control::${cmd}`, value: cmd, numeric };
}

/** Control write mapping entry with expansion metadata. */
export interface CdoControlEntry {
  enumValue: number;
  enumName: string;
  floorsBased: boolean;
  unitsBased: boolean;
}

/**
 * ControlOption view of an adapter-derived control entry, built to the
 * exact shape `enumToControlOptions` produces. Auto-generated rows never
 * pass through the dropdown, so without this their POSTs would omit
 * `command` and their three Command Label/Value/Numeric columns would
 * read "—" while manually-added rows next to them showed values.
 */
export function controlOptionForEntry(entry: CdoControlEntry): ControlOption {
  return {
    label: `Control::${entry.enumName}`,
    value: entry.enumName,
    numeric: entry.enumValue,
  };
}

/**
 * What a `/action-banks/by-bank/:bankId` row needs around it to become a table
 * row: the bank it was fetched for, its display name, and that bank's lift-name
 * lookup. Nothing else — everything the table shows comes off the row itself or
 * the `action` relation the endpoint nests on it.
 */
export interface ActionBankRowContext {
  bankId: string;
  bankName: string;
  liftNames?: Record<string, string>;
}

/** ActionButtonType as its NAME, from either transport's spelling of it. */
function actionButtonTypeName(raw: unknown): string | undefined {
  if (typeof raw === "string" && raw) return raw;
  if (typeof raw === "number") return ActionButtonType[raw] as string | undefined;
  return undefined;
}

/** A generated string field is `string | Uint8Array | null`; only a real string is usable. */
const rowText = (value: unknown): string | undefined =>
  typeof value === "string" && value ? value : undefined;

/** `undefined` for anything that is not a number, so a present 0 survives. */
const rowNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const rowTextList = (value: unknown): string[] | undefined =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : undefined;

/**
 * One `/action-banks/by-bank/:bankId` row as an Actions-table row, or null when
 * the row cannot be identified (or is soft-deleted).
 *
 * Field precedence is "the action_bank row first, its nested `action` relation
 * second". The row owns the per-bank visual configuration — color, timers, and
 * its own copy of label / valid_states, which the Visual Config modal writes —
 * so where both carry a field the row wins. The relation owns the command
 * binding (`cmd`, lift, target position, message_value), which action_bank has
 * no column for.
 *
 * The relation is the ONLY other source: this step never reads
 * `/action-catalogs`, so a row whose `action` the server did not preload simply
 * renders without its Command columns rather than being back-filled from a
 * second list that the endpoint never agreed to.
 */
export function actionBankRowToActionRow(row: any, ctx: ActionBankRowContext): ActionRow | null {
  if (!row || typeof row !== "object") return null;

  const actionId = resolveActionBankActionId(row);
  const messageId = formatUuidT(row.messageId) || String(row.message_id ?? "");
  const bankId = formatUuidT(row.bankId) || String(row.bank_id ?? "") || ctx.bankId;

  // action_bank is a composite-key entity (message_id, bank_id) with no id
  // column of its own. Key the row by the catalog id instead: it is 1:1 with
  // the row and it is what Edit (PUT /actions/:id), Delete and the
  // actionBankMap lookup all address. The composite key is only a fallback so
  // a row whose relation was not preloaded still renders rather than vanishing.
  const id = actionId || (messageId && bankId ? `${messageId}:${bankId}` : "");
  if (!id) return null;

  const action: Record<string, any> =
    row.action && typeof row.action === "object" ? row.action : {};

  // A soft-deleted row is configuration the operator already removed; showing
  // it would invite an Edit that writes to a deleted record.
  if (rowText(row.deletedAt ?? row.deleted_at) || rowText(action.deletedAt ?? action.deleted_at)) {
    return null;
  }

  // The command identity, from the row itself when the catalog relation is
  // absent.
  //
  // The relation is absent on the FLATBUFFER representation, which is what this
  // step actually receives: flatBufferBaseQuery asks for
  // `application/flatbuffers`, ActionBank emits no nested table, and the
  // catalog is where `cmd` and `message_value` live. So every Command column
  // read "—" on precisely the rows the server had answered fully — the value
  // was on the row the whole time, under its own field, and nothing looked
  // there. The JSON shape keeps working: it is still tried first.
  const messageValue =
    rowNumber(action.messageValue ?? action.message_value) ??
    rowNumber((row.message ?? {})?.enumId ?? (row.message ?? {})?.enum_id) ??
    boundCommandValue(row.messageValue ?? row.message_value);
  const toggleMessageValue =
    rowNumber(action.toggleMessageValue ?? action.toggle_message_value) ??
    rowNumber(
      (row.toggleMessage ?? row.toggle_message ?? {})?.enumId ??
        (row.toggleMessage ?? row.toggle_message ?? {})?.enum_id,
    ) ??
    boundCommandValue(row.toggleMessageValue ?? row.toggle_message_value);

  const cmd =
    rowText(action.cmd) ??
    controlCommandName(messageValue, row.messageContentType ?? row.message_content_type) ??
    "";
  const toggleCmd =
    rowText(action.toggleCmd ?? action.toggle_cmd) ??
    controlCommandName(
      toggleMessageValue,
      row.toggleMessageContentType ?? row.toggle_message_content_type,
    );

  const liftId = formatUuidT(action.liftId) || String(action.lift_id ?? "");
  const timer = rowNumber(row.timer);
  const toggleTimer = rowNumber(row.toggleTimer ?? row.toggle_timer);
  const storedCommand = action.command;
  const storedToggleCommand = action.toggleCommand ?? action.toggle_command;

  return {
    key: id,
    id,
    label: rowText(row.label) ?? rowText(action.label) ?? "",
    messageType: cmd,
    bankId,
    bankName: ctx.bankName,
    liftId,
    liftName: (liftId && ctx.liftNames?.[liftId]) || "",
    // How the vendor ENCODES this command, as the server derived it from the
    // bound command's key. Distinct from liftId, which records which car the
    // wizard scoped the action to. Without it the table grouped on an empty
    // lift NAME and called every such row "Bank-Level" — including unit-scoped
    // commands that merely apply to every car, which the monitoring dashboard
    // already routes to the LIFT tab. The two screens disagreed about the same
    // row.
    actionScope: rowNumber(row.actionScope ?? row.action_scope),
    targetPosition:
      rowNumber(action.targetPosition ?? action.target_position) ??
      boundCommandValue(row.targetPosition ?? row.target_position) ??
      0,
    buttonType:
      actionButtonTypeName(row.buttonType ?? row.button_type) ??
      actionButtonTypeName(action.buttonType ?? action.button_type) ??
      (ActionButtonType[ActionButtonType.SWITCH] as string),
    // The table renders a timer cell only for a timer that was actually set;
    // 0 is "none", which is what the manual-add path stores as undefined too.
    timer: timer !== undefined && timer > 0 ? timer : undefined,
    toggleTimer: toggleTimer !== undefined && toggleTimer > 0 ? toggleTimer : undefined,
    toggleMessageType: toggleCmd,
    toggleLabel: rowText(row.toggleLabel ?? row.toggle_label) ?? rowText(action.toggleLabel),
    orgId:
      formatUuidT(row.organizationId) ||
      String(row.organization_id ?? "") ||
      formatUuidT(action.organizationId) ||
      String(action.organization_id ?? "") ||
      undefined,
    // Same story as the command columns: the catalog relation is absent on the
    // FlatBuffer representation, so fall back to the row's own field. It is an
    // Archive.LiftType ordinal there and a string on the JSON shape, hence the
    // generated enum's reverse lookup rather than a hand-written map — a
    // reordered enum then breaks the build instead of renaming every lift.
    liftType: rowText(action.liftType ?? action.lift_type) ?? rowLiftTypeName(row),
    tooltip: rowText(action.tooltip),
    toggleTooltip: rowText(action.toggleTooltip ?? action.toggle_tooltip),
    color: rowText(row.color),
    toggleColor: rowText(row.toggleColor ?? row.toggle_color),
    validStates:
      rowTextList(row.validStates ?? row.valid_states) ??
      rowTextList(action.validStates ?? action.valid_states),
    toggleValidStates:
      rowTextList(row.toggleValidStates ?? row.toggle_valid_states) ??
      rowTextList(action.toggleValidStates ?? action.toggle_valid_states),
    messageValue,
    toggleMessageValue,
    command: isControlOption(storedCommand)
      ? storedCommand
      : deriveControlOption(cmd, messageValue),
    toggleCommand: isControlOption(storedToggleCommand)
      ? storedToggleCommand
      : deriveControlOption(toggleCmd, toggleMessageValue),
  };
}

/**
 * Every row of an `/action-banks/by-bank` response as table rows, tolerating
 * both the `{ data: [...] }` envelope and a bare array.
 *
 * ONE row in, at most one row out. That is the whole point of reading this
 * endpoint rather than action_catalog: an action_bank row references TWO
 * catalog rows (`action_id` and `toggle_action_id`), which are frequently
 * distinct records, so listing catalogs showed a bank with two configured
 * buttons as three table rows — and the extra one was a half of a button, not
 * a button. Rows are de-duplicated by id so two action_bank rows bound to the
 * same catalog cannot collide on the table's rowKey.
 */
export function actionRowsFromActionBanks(
  payload: unknown,
  ctx: ActionBankRowContext,
): ActionRow[] {
  const raw = Array.isArray(payload) ? payload : (payload as any)?.data;
  if (!Array.isArray(raw)) return [];
  const rows: ActionRow[] = [];
  const seen = new Set<string>();
  raw.forEach((entry) => {
    const built = actionBankRowToActionRow(entry, ctx);
    if (!built || seen.has(built.id)) return;
    seen.add(built.id);
    rows.push(built);
  });
  return rows;
}

export function parseCdoControlEntries(adapter: any): CdoControlEntry[] {
  // New binary template format: writes[] with msgId/label as composite key
  const templateWrites: any[] = adapter?.cdo_template?.writes || [];
  // Old JSON format: ln2_to_msg_map[] with ln2_message.msg_id
  const legacyWrites: any[] = adapter?.cdo_input?.ln2_to_msg_map || [];

  const allWrites = [...templateWrites, ...legacyWrites];
  const entries: CdoControlEntry[] = [];

  allWrites.forEach((entry: any) => {
    // Derive composite key: try FlatBuffer unpack (camelCase), API JSON
    // (snake_case), then legacy label/msgId fallback.
    let msgId: string = "";
    const vm = entry?.ln2Message || entry?.ln2_message;
    if (vm?.msg) {
      // Only trust a fully-resolved composite key. Previously, when the
      // strongly-typed extractor couldn't decode the ValuedMessage, this
      // fell back to `${category}:normal:0` — which for control writes
      // fabricated a phantom CarCallLock (enum 0). Leave msgId empty so the
      // label/msgId fallback runs, and skip the entry entirely if nothing
      // resolves, rather than inventing enum 0.
      msgId = valuedMessageTToCompositeKey(vm) || "";
    }
    if (!msgId) {
      msgId = entry?.label || entry?.msgId || entry?.ln2_message?.msg_id || "";
    }
    const parts = msgId.split(":");
    // A control command is always a full composite key
    // "control:<variant>:<enumValue>". Require all three parts so an
    // unresolved key never defaults to enum 0 (CarCallLock).
    if (parts.length >= 3 && parts[0] === "control") {
      const enumVal = parseInt(parts[2], 10);
      if (!Number.isNaN(enumVal)) {
        entries.push({
          enumValue: enumVal,
          enumName: CONTROL_NAME_BY_VALUE.get(enumVal) || `Control_${enumVal}`,
          floorsBased: entry?.floorsBased ?? entry?.floors_based ?? false,
          unitsBased: entry?.unitsBased ?? entry?.units_based ?? true,
        });
      }
    }
  });
  return entries;
}

/**
 * Extracts control entries from a bank's offsetMappingJson.
 * Each entry in the array is a lift's offset map with keys like "control:normal:16".
 * Deduplicates across all lifts.
 */
export function parseOffsetMappingControlEntries(bankData: any): CdoControlEntry[] {
  const raw = bankData?.offsetMappingJson ?? bankData?.offset_mapping;
  if (!raw) return [];
  let mappings: Record<string, number>[];
  try {
    mappings = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return [];
  }
  if (!Array.isArray(mappings)) return [];

  const seen = new Set<number>();
  const entries: CdoControlEntry[] = [];

  mappings.forEach((liftMap: Record<string, number>) => {
    Object.keys(liftMap).forEach((key) => {
      const parts = key.split(":");
      // Only a full composite control key "control:<variant>:<enumValue>"
      // is a control command. A bare "control" key is a MODBUS
      // register-offset entry (category → base register address, e.g.
      // {"traffic":40001,"usage":40001,"control":0}), NOT a command — the
      // old `parts.length >= 3 ? … : 0` default mis-read it as Control
      // enum 0 (CarCallLock) and fabricated a phantom action. Require the
      // enumValue segment so register-offset maps are skipped.
      if (parts.length < 3 || parts[0] !== "control") return;
      const enumVal = parseInt(parts[2], 10);
      if (Number.isNaN(enumVal) || seen.has(enumVal)) return;
      seen.add(enumVal);
      entries.push({
        enumValue: enumVal,
        enumName: CONTROL_NAME_BY_VALUE.get(enumVal) || `Control_${enumVal}`,
        floorsBased: false,
        unitsBased: true,
      });
    });
  });

  return entries;
}

/**
 * Extract control entries from an unpacked MODBUS CDO template.
 *
 * MODBUS adapters do NOT materialize their control commands into the `keys`
 * table (unlike LN1) — the commands live only in the binary CDO template's
 * write entries. `unpackModbusTemplate` decodes each write's ln2Message back
 * into a composite key (`"control:normal:<enumValue>"`) via the strongly-typed
 * `valuedMessageTToCompositeKey`, so here we only need to split that key.
 *
 * Each write also carries floorsBased / unitsBased, which drive auto-gen
 * expansion (per-floor vs per-lift), so they're preserved on the entry.
 * Deduplicates by enumValue.
 */
export function parseModbusTemplateControlEntries(
  template:
    | {
        writes?: { msgId?: string; floorsBased?: boolean; unitsBased?: boolean }[];
      }
    | null
    | undefined,
): CdoControlEntry[] {
  const writes = template?.writes ?? [];
  const seen = new Set<number>();
  const entries: CdoControlEntry[] = [];

  writes.forEach((w) => {
    const parts = (w.msgId ?? "").split(":");
    // Only full composite control keys "control:<variant>:<enumValue>" are
    // commands. Anything shorter is skipped rather than defaulted to enum 0.
    if (parts.length < 3 || parts[0] !== "control") return;
    const enumVal = parseInt(parts[2], 10);
    if (Number.isNaN(enumVal) || seen.has(enumVal)) return;
    seen.add(enumVal);
    entries.push({
      enumValue: enumVal,
      enumName: CONTROL_NAME_BY_VALUE.get(enumVal) || `Control_${enumVal}`,
      floorsBased: w.floorsBased ?? false,
      unitsBased: w.unitsBased ?? true,
    });
  });

  return entries;
}

/**
 * Fetch and decode the MODBUS CDO template for an adapter, returning its
 * control write entries. Non-MODBUS adapters (or a missing/failed fetch)
 * resolve to an empty array — `unpackModbusTemplate` throws on a non-MODBUS
 * payload, which is caught here. Mirrors the CDO-binary source the Channel
 * Modifiers step already uses (`ensureAdapterCdoMappings`).
 */
export async function fetchModbusCdoControlEntries(
  adapterId: string,
  token: string,
): Promise<CdoControlEntry[]> {
  if (!adapterId) return [];
  const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
  try {
    const bytes = await fetchFlatBufferBinary(`${apiBase}/adapters/${adapterId}/cdo`, token);
    if (!bytes || bytes.length === 0) return [];
    return parseModbusTemplateControlEntries(unpackModbusTemplate(bytes));
  } catch {
    return [];
  }
}

// ************************************************ */
// COMPONENT ************************************** */
export default function StepActions() {
  const dispatch = useAppDispatch();
  const { theme } = useSelector((state: RootState) => state.home);
  const { state: wizardState, setState: setWizardState, markStepCompleted, goToStep } = useWizard();

  // ************************************************ */
  // LOCAL STATE ************************************ */
  // The "Add Manual Action" form used to carry one useState per field
  // (label, messageType, tooltip, toggleTooltip, actionColor,
  // toggleActionColor, buttonType, timer, toggleTimer,
  // toggleMessageType, toggleLabel). Consolidated into a single
  // `bankAction` object — named/shaped after BankActionInterface
  // (color/timer/label/toggleColor/toggleTimer/toggleLabel/buttonType)
  // plus the UI-only fields the form also needs (messageType,
  // toggleMessageType, tooltip, toggleTooltip) that aren't persisted on
  // BankActionInterface itself. `updateBankActionField` is the single
  // setter every input in the form goes through.
  const [bankAction, setBankAction] = useState<ManualActionFormState>({
    ...BLANK_MANUAL_ACTION_FORM,
  });
  const updateBankActionField = <K extends keyof ManualActionFormState>(
    field: K,
    value: ManualActionFormState[K],
  ) => {
    setBankAction((prev) => ({ ...prev, [field]: value }));
  };
  const [selectedBankId, setSelectedBankId] = useState<string | undefined>(undefined);
  // "All to all banks": send the action being composed to every elevator bank
  // instead of one. Off by default; turning it on takes over the Bank select.
  const [addToAllBanks, setAddToAllBanks] = useState(false);
  // In-flight guard for the all-banks fan-out (N POSTs + N re-reads).
  const [isAddingToAllBanks, setIsAddingToAllBanks] = useState(false);
  const [selectedLiftId, setSelectedLiftId] = useState<string | undefined>(undefined);
  const [actions, setActions] = useState<ActionRow[]>([]);

  // Adapter control keys filter (for manual add)
  const [adapterControlOptions, setAdapterControlOptions] = useState<ControlOption[] | undefined>(
    undefined,
  );
  /**
   * Catalog ids for every message THIS BANK'S ADAPTER declares, used to scope
   * the Expected Response picker.
   *
   * Built from the adapter's own `keys` rows — each carries a `messageContent`
   * and a `messageValue`, which is exactly a (category, member) pair — plus the
   * Control commands the binary-CDO and offset-mapping sources contribute, so
   * MODBUS/OPCUA/LV2/SEQUENT banks (which have no `keys` rows at all) are not
   * left with an empty list.
   *
   * EMPTY means the adapter declared nothing we could map, NOT "no messages":
   * the picker falls back to the full catalog in that case and says so, because
   * a control an operator cannot use is worse than an unfiltered one.
   */
  const [adapterMessageIds, setAdapterMessageIds] = useState<string[]>([]);
  // Operator opt-in to the full Control catalog when the adapter under-reports.
  // Resets on bank change so the escape hatch never silently carries over to a
  // bank whose adapter reports its commands correctly.
  const [showAllCommands, setShowAllCommands] = useState(false);

  // Visual Config modal state — opens from the per-row "Visual" button
  // and lets operators edit the matching action_bank row's color /
  // valid-states config without leaving the wizard. The map is keyed
  // by the action_catalog row id (==action_bank.action_id) so the
  // table render can check both "does an action_bank row exist for
  // this row?" (enables the button) and "what's the current visual
  // config?" (pre-fills the modal on open).
  const [actionBankMap, setActionBankMap] = useState<Map<string, ActionBankT>>(new Map());
  const [visualModalOpen, setVisualModalOpen] = useState(false);
  const [visualSaving, setVisualSaving] = useState(false);
  const [visualError, setVisualError] = useState<string | null>(null);
  const [editingVisualRowId, setEditingVisualRowId] = useState<string | null>(null);
  const [visualColor, setVisualColor] = useState("");
  const [visualColor2, setVisualColor2] = useState("");
  const [visualToggleColor, setVisualToggleColor] = useState("");
  const [visualTimer, setVisualTimer] = useState<number>(0);
  const [visualTimer2, setVisualTimer2] = useState<number>(0);
  const [visualToggleTimer, setVisualToggleTimer] = useState<number>(0);
  const [visualValidStates, setVisualValidStates] = useState<string[]>([]);
  const [visualToggleValidStates, setVisualToggleValidStates] = useState<string[]>([]);
  const [visualLabel, setVisualLabel] = useState("");
  const [visualToggleLabel, setVisualToggleLabel] = useState("");

  // MCE pre-fill state — tracks whether the selected bank's adapter is MCE
  const [isMceBank, setIsMceBank] = useState(false);
  // MODBUS state — tracks whether the selected bank's adapter is MODBUS, so
  // the manual-add dropdown can fall back to the full Control catalog when the
  // adapter's binary CDO decodes to zero control writes (mirrors the MCE
  // escape hatch), rather than hard-blocking the operator.
  const [isModbusBank, setIsModbusBank] = useState(false);

  const [stepError, setStepError] = useState<string | null>(null);

  // Whether the create the operator just submitted actually materialized a
  // button (see actionMaterialization.ts). Declared here so it exists before
  // the RTK Query hooks below reference it; populated by the effect further
  // down, once `isLoadingPost`/`isSuccessPost` exist.
  const [verifiedActionSuccess, setVerifiedActionSuccess] = useState(false);

  // Id of the create whose verification is still allowed to apply its
  // result. The verification effect below awaits a real network round trip
  // (refreshBankRows), so a second single-bank add started before the first
  // one's verification finishes leaves two in flight at once; without this,
  // whichever resolves LAST wins regardless of which add it belongs to,
  // which can overwrite a newer add's toast/error/form-clear with a stale
  // one's. Only the verification whose actionId still matches this ref when
  // it resolves is allowed to write state.
  const latestPendingActionIdRef = useRef<string | null>(null);

  // ************************************************ */
  // EDIT / DELETE STATE **************************** */
  // Edit modal targets one row at a time. When `editingRow` is non-null
  // the modal is visible and `editXxx` state mirrors the row's current
  // values so the operator can mutate them in isolation before SAVE.
  const [editingRow, setEditingRow] = useState<ActionRow | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editMessageType, setEditMessageType] = useState<string | undefined>(undefined);
  const [editButtonType, setEditButtonType] = useState<WizardButtonType>("SINGLE");
  const [editTimer, setEditTimer] = useState<number>(0);
  const [editToggleTimer, setEditToggleTimer] = useState<number>(0);
  const [editToggleMessageType, setEditToggleMessageType] = useState<string | undefined>(undefined);
  const [editToggleLabel, setEditToggleLabel] = useState<string>("");
  const [editLiftId, setEditLiftId] = useState<string | undefined>(undefined);
  // Cached adapter command options for the edited row's bank (so Edit
  // can re-use the same Control commands the operator originally chose
  // from).
  const [editCommandOptions, setEditCommandOptions] = useState<ControlOption[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // The banks whose `/action-banks/by-bank` rows are already in `actions`,
  // so the load effect asks the server once per bank and not once per render.
  //
  // A Set rather than a boolean: `wizardState.bankIds` fills in progressively
  // as the wizard hydrates its own state, so the effect routinely runs first
  // with a PARTIAL bank list. A single "already hydrated" flag latched on that
  // first partial pass and every bank that arrived afterwards was never
  // fetched — its rows existed on the server, its GET never happened, and its
  // table silently stayed empty. Tracking per bank means a late-arriving bank
  // is simply a bank not in the set yet.
  const loadedBanksRef = useRef<Set<string>>(new Set());
  // Banks with a request already in flight, so a re-run of the load effect
  // does not ask for the same bank a second time.
  const loadingBanksRef = useRef<Set<string>>(new Set());
  // Always the CURRENT markStepCompleted, reachable from the load effect
  // without listing it as a dependency. WizardContext hands out a new function
  // identity on every render, so depending on it directly re-runs the effect
  // on every wizard state change — see the load effect for what that cost.
  const markStepCompletedRef = useRef(markStepCompleted);
  markStepCompletedRef.current = markStepCompleted;
  // Cache of per-bank Control command options used by the Edit modal.
  // Bank + adapter-keys are stable for the wizard's lifetime, but RTK
  // Query is configured `keepUnusedDataFor: 0` so without this map every
  // Edit click would round-trip both APIs. Keyed by bankId.
  const editCommandOptionsCacheRef = useRef<Map<string, ControlOption[]>>(new Map());

  // ************************************************ */
  // DRAFT PERSISTENCE ****************************** */
  // The action-builder form (label, message type, tooltip, target lift
  // position) is one of the most state-heavy single-action forms in the
  // wizard. Persist the operator's in-progress action so a reload
  // doesn't blow away their composition work. The `actions` list itself
  // is server-fetched; only the form draft is preserved here.
  //
  // Verification: bank/lift ids are validated against the wizard's
  // current entity lists before restore (stale ids would just remain
  // undefined). String fields restore as-is — they're operator input
  // and a previous typo is recoverable.
  useWizardStepDraft<
    ManualActionFormState & {
      selectedBankId: string | undefined;
      selectedLiftId: string | undefined;
    }
  >({
    orgId: wizardState.organizationId,
    step: WizardStep.Actions,
    scopeId: "actions-form",
    scopeType: "step",
    draft: {
      ...bankAction,
      selectedBankId,
      selectedLiftId,
    },
    onRestore: (saved) => {
      setBankAction((prev) => {
        const next = { ...prev };
        if (typeof saved.label === "string") next.label = saved.label;
        if (typeof saved.messageType === "string") next.messageType = saved.messageType;
        if (typeof saved.tooltip === "string") next.tooltip = saved.tooltip;
        if (typeof saved.toggleTooltip === "string") next.toggleTooltip = saved.toggleTooltip;
        if (typeof saved.color === "string") next.color = saved.color;
        if (typeof saved.toggleColor === "string") next.toggleColor = saved.toggleColor;
        if (typeof saved.buttonType === "string" && isWizardButtonType(saved.buttonType)) {
          next.buttonType = saved.buttonType;
        }
        if (typeof saved.timer === "number" && saved.timer >= 0) next.timer = saved.timer;
        // Optional in old drafts (field added later) — missing restores as 0.
        if (typeof saved.toggleTimer === "number" && saved.toggleTimer >= 0) {
          next.toggleTimer = saved.toggleTimer;
        }
        if (typeof saved.toggleMessageType === "string") {
          next.toggleMessageType = saved.toggleMessageType;
        }
        if (typeof saved.toggleLabel === "string") next.toggleLabel = saved.toggleLabel;
        // Optional in old drafts (fields added later) — a draft written
        // before `command`/`toggleCommand` existed simply restores them
        // as undefined, same as a never-touched dropdown.
        if (isControlOption(saved.command)) next.command = saved.command;
        if (isControlOption(saved.toggleCommand)) next.toggleCommand = saved.toggleCommand;
        if (Array.isArray(saved.validStates)) next.validStates = saved.validStates;
        if (Array.isArray(saved.toggleValidStates))
          next.toggleValidStates = saved.toggleValidStates;
        // Optional in old drafts (fields added later) — a draft written before
        // expected-response verification existed restores as "nominates
        // nothing", which is the same thing a never-touched picker means.
        if (Array.isArray(saved.expectedResponse)) next.expectedResponse = saved.expectedResponse;
        if (typeof saved.expectedResponseTimeout === "number" && saved.expectedResponseTimeout >= 0)
          next.expectedResponseTimeout = saved.expectedResponseTimeout;
        return next;
      });
      if (
        typeof saved.selectedBankId === "string" &&
        wizardState.bankIds.includes(saved.selectedBankId)
      ) {
        setSelectedBankId(saved.selectedBankId);
        // A draft-restored bank arrives here WITHOUT going through
        // handleBankChange, so without this its Control Command picker never
        // loads (sits on "Loading...") and Sync/Add stay disabled. Load the
        // bank's control context — the PURE loader, no create/delete — so the
        // manual controls work. There's no reconcile/seed step left to race
        // here — mount-time auto-generation is gone — but a draft-restore
        // callback would be the wrong place to call one even if there were:
        // restoring saved fields shouldn't have side effects on the server,
        // so only the pure loader runs.
        loadBankControlContext(saved.selectedBankId).catch(() => {
          // Best-effort: on failure the picker simply stays unloaded, exactly
          // as before this fix — the operator can re-select the bank to retry.
        });
      }
      if (typeof saved.selectedLiftId === "string") {
        // Validate the lift id against ALL lifts in ALL banks of this
        // wizard — the bank list above narrows by selection but a
        // previously chosen lift may belong to any restored bank.
        const allLiftIds = Object.values(wizardState.liftIdsByBank).flat();
        if (allLiftIds.includes(saved.selectedLiftId)) {
          setSelectedLiftId(saved.selectedLiftId);
        }
      }
    },
  });

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [
    triggerPostAction,
    {
      isSuccess: isSuccessPost,
      isLoading: isLoadingPost,
      isError: isErrorPost,
      error: errorPost,
      data: dataPost,
    },
  ] = usePostActionMutation();

  // Reset before each new attempt so a second add that also verifies is a
  // real false→true transition for GlobalAlert2 (it only notifies on change),
  // and so a prior success's toast can't linger through a failed retry.
  useEffect(() => {
    if (isLoadingPost) {
      setVerifiedActionSuccess(false);
    }
  }, [isLoadingPost]);

  const [
    triggerPutAction,
    { isLoading: isLoadingPut, isSuccess: isSuccessPut, isError: isErrorPut, error: errorPut },
  ] = usePutActionMutation();
  const [triggerDeleteAction, { isError: isErrorDelete, error: errorDelete }] =
    useDeleteActionMutation();

  // action_bank hooks — by-bank fetch hydrates actionBankMap (used by
  // the Visual button's enable-check + Visual modal's pre-fill); the
  // PUT writes the operator's visual config edits back to the server.
  const [triggerPutActionBank] = usePutActionBankMutation();

  const [triggerGetBank] = useLazyGetBankQuery();
  const [triggerGetAdapterKeys] = useLazyGetAdapterKeysQuery();

  // Auto-mark this step complete whenever the operator is on it.
  // The action catalog is optional — operators routinely complete a
  // wizard with no custom actions and add them later from the
  // dashboard. Real action creations still go through handleAdd /
  // handleBatchAdd; this just unblocks Next when the operator
  // chooses to add nothing. Depending on `currentStep` re-fires this
  // if an upstream step unmarked us.
  useEffect(() => {
    if (wizardState.currentStep === WizardStep.Actions) {
      markStepCompleted(WizardStep.Actions);
    }
  }, [wizardState.currentStep, markStepCompleted]);

  // ************************************************ */
  // FUNCTIONS ************************************* */
  const clearForm = () => {
    setBankAction({ ...BLANK_MANUAL_ACTION_FORM });
    setSelectedLiftId(undefined);
  };

  /**
   * Per-button-type validation. Timer types need a positive timer,
   * TOGGLETIMER additionally needs its second timer, and every type
   * with a mandatory off command needs the toggle message bound —
   * otherwise the runtime renders a second button with nothing to send.
   */
  const isFormValid = () => {
    if (bankAction.label.trim() === "" || !bankAction.messageType) return false;
    // One bank, or every elevator bank — but at least one real target.
    if (addToAllBanks ? allElevatorBankIds.length === 0 : !selectedBankId) return false;
    if (needsTimer(bankAction.buttonType) && bankAction.timer <= 0) return false;
    if (needsToggleTimer(bankAction.buttonType) && bankAction.toggleTimer <= 0) return false;
    if (needsToggle(bankAction.buttonType) && !bankAction.toggleMessageType) return false;
    return true;
  };

  const derivedLiftType = (selectedBankId && wizardState.bankTypes?.[selectedBankId]) || "";

  /**
   * Every ELEVATOR bank in the wizard — the targets of "All to all banks".
   *
   * Escalator banks are excluded on purpose: the Control commands this form
   * offers are elevator commands, and an escalator controller has nothing to do
   * with a car call or a hall call. A bank whose type the wizard never recorded
   * is left out too rather than guessed at.
   */
  const allElevatorBankIds = useMemo(
    () => wizardState.bankIds.filter((id) => wizardState.bankTypes?.[id] === "ELEVATOR"),
    [wizardState.bankIds, wizardState.bankTypes],
  );

  /**
   * Flip "All to all banks". Turning it ON drops the bank selection so nothing
   * downstream can still read a single bank as the target; turning it OFF
   * leaves the form as-is for the operator to pick a bank again.
   */
  const handleAddToAllBanksChange = (checked: boolean) => {
    setAddToAllBanks(checked);
    if (!checked) return;
    setSelectedBankId(undefined);
    setSelectedLiftId(undefined);
  };

  /**
   * Fetch action_bank rows for every given bank, merge them into
   * actionBankMap, and hand the raw rows back to the caller.
   *
   * These rows are BOTH things at once: the table's dataSource (via
   * actionRowsFromActionBanks) and the lookup behind the Visual Config modal
   * and the Color / Toggle Color / Timer / Toggle Timer / Toggle Valid States
   * columns, which action_catalog has no columns for. loadBankControlContext
   * only ever loads the ONE bank the operator just selected, so a table that
   * groups every bank's rows together would otherwise only have those columns
   * for whichever bank happened to be loaded last.
   *
   * A bank whose request FAILS is reported as such rather than as an empty
   * bank: callers drop a failed bank instead of rendering it as empty, so a
   * transient failure never reads as "this bank has no actions".
   */
  const loadActionBankRowsForBanks = async (
    bankIds: string[],
    token: string,
  ): Promise<{ bankId: string; rows: any[]; failed: boolean }[]> => {
    if (bankIds.length === 0) return [];
    const perBank = await Promise.all(
      bankIds.map((bankId) => {
        // `dispatch(endpoints.…initiate())`, NOT the useLazyQuery trigger.
        //
        // A lazy-query hook owns ONE subscription slot, so triggering it N
        // times concurrently makes each call supersede the one before it. The
        // superseded calls still RESOLVE — no error, every request returns
        // 200 — but they resolve with no data, because their cache entry was
        // already evicted (this API sets keepUnusedDataFor: 0). Only the
        // last-triggered bank keeps its rows. With 14 banks that is 13 tables
        // silently rendering empty while the network tab shows 14 successful
        // responses full of rows.
        //
        // `initiate` creates an independent request and subscription per
        // call, which is what "fetch these N banks at once" actually needs.
        // Each one is unsubscribed as soon as its data has been read.
        const request = dispatch(
          actionBankAPI.endpoints.getActionBanksByBank.initiate(
            { bankId, token },
            { forceRefetch: true },
          ),
        );
        return request
          .unwrap()
          .then((result: { data?: ActionBankT[] } | ActionBankT[] | undefined) => {
            const raw = Array.isArray(result) ? result : result?.data;
            return { bankId, rows: Array.isArray(raw) ? (raw as any[]) : [], failed: false };
          })
          .catch(() => ({ bankId, rows: [] as any[], failed: true }))
          .finally(() => request.unsubscribe());
      }),
    );
    setActionBankMap((prev) => {
      const next = new Map(prev);
      perBank.forEach((entry) => {
        entry.rows.forEach((ab) => {
          const key = resolveActionBankActionId(ab);
          if (key) next.set(key, ab);
        });
      });
      return next;
    });
    return perBank;
  };

  /**
   * Release a dispatched mutation's store entry once its result has been read.
   *
   * `dispatch(endpoint.initiate(...))` for a MUTATION keeps its result in the
   * store until reset() is called — unlike a query subscription, nothing cleans
   * it up. Returns the same handle so it can be chained.
   */
  function withMutationReset<T extends { unwrap: () => Promise<unknown>; reset: () => void }>(
    handle: T,
  ): T {
    handle.unwrap().finally(() => handle.reset());
    return handle;
  }

  /** The naming context a `/action-banks/by-bank` row needs to become a table row. */
  const bankRowContext = (bankId: string): ActionBankRowContext => ({
    bankId,
    bankName: wizardState.bankNames?.[bankId] || "",
    liftNames: wizardState.liftNamesByBank?.[bankId],
  });

  /**
   * Re-read ONE bank from `/action-banks/by-bank/:bankId` and replace that
   * bank's rows in the table with the answer.
   *
   * Every create / edit / delete ends here rather than patching `actions`
   * locally. That is what makes the endpoint the single source of truth in
   * practice and not just on mount: a row is on screen because the server just
   * returned it, so a write the server rejected, coerced, or expanded into a
   * different set of rows can never leave the table disagreeing with it.
   *
   * Other banks' rows are untouched, and a failed request leaves the table as
   * it was — an empty answer we could not obtain must not read as "this bank
   * is now empty".
   */
  const refreshBankRows = async (bankId: string): Promise<ActionRow[] | null> => {
    if (!bankId) return null;
    try {
      const token = await GETJwtToken();
      const [entry] = await loadActionBankRowsForBanks([bankId], token);
      if (!entry || entry.failed) return null;
      const fresh = actionRowsFromActionBanks(entry.rows, bankRowContext(bankId));
      setActions((prev) => [...prev.filter((row) => row.bankId !== bankId), ...fresh]);
      return fresh;
    } catch {
      // Best-effort: the table keeps showing the last answer the server gave.
      return null;
    }
  };

  /**
   * Load a bank's control-command context from its adapter and populate the
   * per-bank UI state (lifts, Control Command options, syncable entries, the
   * action_bank lookup map). PURE with respect to action rows — it performs no
   * create or delete, so the draft-restore path can call it freely.
   */
  const loadBankControlContext = async (bankId: string): Promise<void> => {
    const token = await GETJwtToken();

    // Resolve the adapter ID for this bank
    const adapterId = wizardState.adapterIdByBank[bankId] || "";

    // Fetch bank data + lifts + adapter keys in parallel
    const [bankResult, keysResult] = await Promise.all([
      triggerGetBank({ bankId, token }),
      adapterId ? triggerGetAdapterKeys({ adapterId, token }) : Promise.resolve({ data: [] }),
    ]);

    const bankData = bankResult.data?.data || bankResult.data;

    // Detect MCE adapter for pre-fill button visibility
    setIsMceBank(isMceAdapter(bankData?.adapter));
    // Detect MODBUS from the bank's adapter metadata OR the wizard's
    // per-bank protocol map. MODBUS banks usually arrive with no nested
    // `adapter` on the bank response (their control commands live only in the
    // separate binary CDO), so the metadata map is the reliable signal.
    setIsModbusBank(
      isModbusAdapter(bankData?.adapter) ||
        (wizardState.adapterProtocolByBank?.[bankId] ?? "").toUpperCase() === "MODBUS",
    );

    // Build control entries from multiple sources
    const controlValues = new Set<number>();

    // Source 1: offsetMappingJson from the bank response
    const offsetEntries = parseOffsetMappingControlEntries(bankData);
    offsetEntries.forEach((e) => controlValues.add(e.enumValue));

    // Source 2: adapter keys fetched separately
    const keysRaw: unknown = (keysResult.data as any)?.data ?? keysResult.data ?? [];
    const adapterKeys: any[] = Array.isArray(keysRaw) ? keysRaw : [];
    adapterKeys.forEach((k: any) => {
      if (k.messageContent === MessageContent.Control_ControlMessage) {
        controlValues.add(k.messageValue);
      }
    });

    // Source 3: CDO template/input entries from adapter (if nested)
    const cdoEntries = parseCdoControlEntries(bankData?.adapter);
    cdoEntries.forEach((e) => controlValues.add(e.enumValue));

    // Source 4: binary CDO. MODBUS / OPCUA / LOBBYVISION2 / SEQUENT adapters
    // keep their control commands ONLY in their protocol-specific binary CDO
    // (no `keys` rows and no nested adapter on the bank response), so without
    // this the writes an operator added — Car to Lobby, Independent Service,
    // etc. — never surface in the actions UI. Fetched via the same
    // /adapters/:id/cdo endpoint; resolves empty for non-CDO adapters.
    const modbusEntries = await fetchBinaryCdoControlEntries(
      resolveProtocolKey(wizardState.adapterProtocolByBank?.[bankId], bankData?.adapter),
      adapterId,
      token,
    );
    modbusEntries.forEach((e) => controlValues.add(e.enumValue));

    const filtered = ALL_CONTROL_OPTIONS.filter((opt) => controlValues.has(opt.numeric));
    setAdapterControlOptions(filtered);

    // What this bank's adapter can REPORT, for the Expected Response picker.
    // Every key category, not just Control — plus the Control commands the
    // binary-CDO and offset-mapping sources contributed above, which is the
    // only thing a MODBUS/OPCUA/LV2/SEQUENT bank has (no `keys` rows at all).
    setAdapterMessageIds(adapterDeclaredMessageIds(adapterKeys, filtered));
  };

  /**
   * Select a bank: load the adapter context that fills the Add form's Control
   * Command dropdown, and re-read the bank's rows from
   * `/action-banks/by-bank/:bankId`.
   *
   * Reads only. The step has no path that creates or deletes an action on its
   * own any more — every write is one the operator asked for through the Add
   * form, the Edit modal, or a row's Delete button.
   */
  /**
   * Say which scope the Expected Response picker is actually under.
   *
   * An unfiltered picker and an adapter-scoped one look identical, and the
   * difference decides whether a nominated message can ever arrive. Silence
   * here would leave an operator to discover it from an action that reads
   * "No confirmation" forever.
   */
  const expectedResponseScopeHelp = (): string => {
    if (!selectedBankId) return "Select a bank to scope this list to its adapter.";
    if (adapterMessageIds.length > 0) {
      return `Only the ${adapterMessageIds.length} messages this bank's adapter declares. Leave empty for no verification.`;
    }
    return "This bank's adapter declares no message keys, so every message is offered — check the adapter can actually report the one you pick.";
  };

  const handleBankChange = async (bankId: string) => {
    setSelectedBankId(bankId);
    setSelectedLiftId(undefined);
    // Clear the picked option alongside the command string — the new bank
    // has its own command list, so a leftover `command` object would ship
    // a label/numeric for a command that is no longer selected.
    setBankAction((prev) => ({ ...prev, messageType: undefined, command: undefined }));
    setAdapterControlOptions(undefined);
    setAdapterMessageIds([]);
    setShowAllCommands(false);
    setIsMceBank(false);
    setIsModbusBank(false);

    await loadBankControlContext(bankId);
    await refreshBankRows(bankId);
  };

  /**
   * Resolve the Control enum value behind a selected command string so
   * the backend can materialize the runtime Message/ActionBank rows
   * without adapter-key archaeology. commandOptions carries the numeric
   * straight from the generated Control enum / adapter parse.
   */
  const numericForCommand = (cmd: string | undefined): number | undefined =>
    resolveNumericForCommand(commandOptions, cmd);

  /**
   * Stamp the current form's button-behavior fields (timer, toggle
   * timer, toggle command/label + their Control enum values) onto a
   * catalog payload. Shared by the single Add form and every bulk-add
   * path so all creation flavors carry identical per-type fields.
   * SWITCH accepts an optional timed-off sequence, so its timer and
   * toggle ride along whenever set even though needsTimer/needsToggle
   * exclude it.
   */
  const buttonBehaviorFields = (): Record<string, unknown> => {
    const fields: Record<string, unknown> = {};
    if (usesTimerField(bankAction.buttonType) && bankAction.timer > 0) {
      fields.timer = bankAction.timer;
    }
    if (needsToggleTimer(bankAction.buttonType) && bankAction.toggleTimer > 0) {
      fields.toggle_timer = bankAction.toggleTimer;
    }
    if (usesToggleField(bankAction.buttonType) && bankAction.toggleMessageType) {
      fields.toggle_cmd = bankAction.toggleMessageType;
      const toggleNumeric = numericForCommand(bankAction.toggleMessageType);
      if (toggleNumeric !== undefined) fields.toggle_message_value = toggleNumeric;
      // Full picked option — {label, value, numeric} — alongside the
      // split-out toggle_cmd / toggle_message_value it is derived from.
      const toggleCommandObject =
        bankAction.toggleCommand ??
        resolveControlOptionForCommand(commandOptions, bankAction.toggleMessageType);
      if (toggleCommandObject) fields.toggle_command = toggleCommandObject;
      if (bankAction.toggleLabel.trim()) fields.toggle_label = bankAction.toggleLabel.trim();
    }
    return fields;
  };

  /**
   * The action_catalog body for one target bank, built from the current form.
   *
   * `liftId` is only ever the operator's explicit pick on the single-bank path:
   * a lift belongs to one bank, so it means nothing for any other target.
   */
  const buildManualActionBody = (bankId: string, liftId?: string): Record<string, unknown> => {
    const body: Record<string, unknown> = {
      label: bankAction.label,
      cmd: bankAction.messageType,
      tooltip: bankAction.tooltip,
      lift_type: wizardState.bankTypes?.[bankId] || "",
      // target_position is intentionally always 0 — the floor is
      // resolved by the floor-button strip in the monitoring UI.
      target_position: 0,
      valid_states: bankAction.validStates,
      // The verification contract. ALWAYS sent, empty list included: the
      // backend's map-based update writes provided values verbatim, so omitting
      // the field on an edit that cleared it would leave the old nomination in
      // place — the same explicit-zero-value rule toggle_cmd/toggle_label
      // already follow. The timeout rides along only when a nomination exists,
      // since 0 is the UNSET sentinel and means nothing on its own.
      expected_response: bankAction.expectedResponse,
      expected_response_timeout:
        bankAction.expectedResponse.length > 0 ? bankAction.expectedResponseTimeout : 0,
      bank_id: bankId,
      org_id: wizardState.organizationId,
      button_type: wizardButtonTypeToString(bankAction.buttonType),
    };
    const messageValue = numericForCommand(bankAction.messageType);
    if (messageValue !== undefined) {
      body.message_value = messageValue;
    }
    // Whole option object from the Control Command dropdown. The `??`
    // covers a form restored from a pre-`command` draft, where only the
    // command string round-tripped.
    const commandObject =
      bankAction.command ?? resolveControlOptionForCommand(commandOptions, bankAction.messageType);
    if (commandObject) {
      body.command = commandObject;
    }
    if (liftId) {
      body.lift_id = liftId;
    }
    if (bankAction.color) {
      body.color = bankAction.color;
    }
    Object.assign(body, buttonBehaviorFields());
    if (usesToggleField(bankAction.buttonType)) {
      if (bankAction.toggleTooltip) body.toggle_tooltip = bankAction.toggleTooltip;
      if (bankAction.toggleColor) body.toggle_color = bankAction.toggleColor;
      if (bankAction.toggleValidStates.length > 0) {
        body.toggle_valid_states = bankAction.toggleValidStates;
      }
    }
    return body;
  };

  /**
   * Create the composed action on every elevator bank — one POST per bank, the
   * same command and appearance on each.
   *
   * Dispatched per bank rather than fired through the mutation hook: the hook
   * carries ONE isSuccess/data pair, so N triggers would leave the success
   * effect seeing a single result and adding a single row. Here the whole fan-out
   * is awaited and the affected banks are re-read from
   * `/action-banks/by-bank/:bankId`, so the table again shows what the server
   * holds rather than what we believe we sent.
   *
   * Partial failure is reported rather than swallowed: the banks that took the
   * action keep it, and the operator is told which ones did not.
   */
  const addActionToAllBanks = async (token: string) => {
    const results: { bankId: string; ok: boolean; rendered: boolean }[] = await Promise.all(
      allElevatorBankIds.map((bankId) =>
        // The mutation entry is reset once its answer has been read. Without
        // it, dispatching initiate() for a mutation leaves one entry per call
        // in the store's mutations slice for the life of the session, and a
        // cross-bank add on a large site dispatches one per bank every press.
        withMutationReset(
          dispatch(
            actionAPI.endpoints.postAction.initiate({ body: buildManualActionBody(bankId), token }),
          ),
        )
          .unwrap()
          .then((response: unknown) => {
            // A bank can accept the catalog row and still not render a button,
            // so "ok" and "rendered" are separate answers. An older server
            // sends no verdict; that is read as rendered rather than warned
            // about, since warning on every create would train the operator to
            // ignore the warning.
            const status = readMaterializationStatus(response);
            return { bankId, ok: true, rendered: status ? willRender(status) : true };
          })
          .catch(() => ({ bankId, ok: false, rendered: false })),
      ),
    );

    const nameFor = (bankId: string) => wizardState.bankNames?.[bankId] || bankId;
    const failed = results.filter((result) => !result.ok);
    const unrendered = results.filter((result) => result.ok && !result.rendered);

    // Reported separately: a bank that rejected the action needs a retry, while
    // a bank that took it but rendered no button needs someone to look at
    // whether its controller accepts that command. Collapsing them into one
    // count would send the operator down the wrong path for half the list.
    if (failed.length > 0) {
      setStepError(
        `Failed to add the action to ${failed.length} bank(s): ${failed
          .map((result) => nameFor(result.bankId))
          .join(", ")}`,
      );
    } else if (unrendered.length > 0) {
      setStepError(
        `Saved on ${unrendered.length} bank(s) but no button was created, so the action will ` +
          `not appear in monitoring: ${unrendered
            .map((result) => nameFor(result.bankId))
            .join(", ")}`,
      );
    }

    const created = results.filter((result) => result.ok).map((result) => result.bankId);
    if (created.length > 0) {
      markStepCompleted(WizardStep.Actions);
      await Promise.all(created.map((bankId) => refreshBankRows(bankId)));
    }

    // The form survives whenever anything needs the operator's attention, so a
    // retry does not mean retyping the whole action.
    if (failed.length === 0 && unrendered.length === 0) {
      clearForm();
    }
  };

  const handleAddAction = async () => {
    if (!isFormValid()) return;
    const token = await GETJwtToken();
    if (addToAllBanks) {
      setIsAddingToAllBanks(true);
      try {
        await addActionToAllBanks(token);
      } finally {
        setIsAddingToAllBanks(false);
      }
      return;
    }
    if (!selectedBankId) return;
    // Single bank keeps the mutation hook: its isSuccess/data pair is what the
    // effect below reads to record the new catalog id and re-read the bank.
    triggerPostAction({ body: buildManualActionBody(selectedBankId, selectedLiftId), token });
  };

  const handleSkip = () => {
    markStepCompleted(WizardStep.Actions);
    // Originally `goToStep(11)` — Actions used to live at index 10,
    // so 11 meant "next step (MonitorGrid)." After ChannelModifiers
    // was inserted at index 5 the literal still pointed at the
    // *current* step, leaving operators stuck on Actions when they
    // clicked Skip. Use the enum for the actual next step.
    goToStep(WizardStep.MonitorGrid);
  };

  /**
   * Map the stored ActionButtonType enum-name string back onto the
   * wizard's button-type choice. Anything not exposed in the wizard
   * surface (HOLDBACK / TURNBACK / ENERGIZER_TURN_BACK) falls back to
   * SINGLE so editing those rows here doesn't silently downgrade them.
   * The dedicated Bank Actions screen remains the place to choose the
   * full variant set.
   *
   * SINGLE and SWITCH both write ActionButtonType.SWITCH on the wire, so a
   * stored "SWITCH" row can't be told apart by button_type alone —
   * `hasToggleCommand` (whether the row has a toggle/off command bound)
   * disambiguates: SWITCH's off-sequence preset is the only one of the two
   * that ever binds one, so its presence means the row was created as
   * SWITCH rather than the plain SINGLE preset.
   */
  /**
   * Populate edit-modal state from the clicked row and fetch the bank's
   * Control command list (with caching) so the operator can re-select
   * cmd if desired. timer / toggleMessageType / toggleLabel pre-fill
   * from the row so a TOGGLE/TOGGLETIMER edit followed by an immediate
   * Save preserves the conditional state instead of stripping it.
   */
  const openEditModal = async (row: ActionRow) => {
    setEditingRow(row);
    setEditLabel(row.label);
    setEditMessageType(row.messageType || undefined);
    setEditButtonType(wizardButtonTypeFromString(row.buttonType, Boolean(row.toggleMessageType)));
    setEditTimer(row.timer ?? 0);
    setEditToggleTimer(row.toggleTimer ?? 0);
    setEditToggleMessageType(row.toggleMessageType);
    setEditToggleLabel(row.toggleLabel ?? "");
    setEditLiftId(row.liftId || undefined);

    const bankId = row.bankId;
    if (!bankId) {
      setEditCommandOptions([]);
      return;
    }

    const cached = editCommandOptionsCacheRef.current.get(bankId);
    if (cached) {
      setEditCommandOptions(cached);
      return;
    }
    setEditCommandOptions([]);

    try {
      const token = await GETJwtToken();
      const adapterId = wizardState.adapterIdByBank[bankId] || "";
      const [bankResult, keysResult] = await Promise.all([
        triggerGetBank({ bankId, token }),
        adapterId ? triggerGetAdapterKeys({ adapterId, token }) : Promise.resolve({ data: [] }),
      ]);
      const bankData = bankResult.data?.data || bankResult.data;
      const controlValues = new Set<number>();
      parseOffsetMappingControlEntries(bankData).forEach((e) => controlValues.add(e.enumValue));
      parseCdoControlEntries(bankData?.adapter).forEach((e) => controlValues.add(e.enumValue));
      // Binary-CDO control commands (MODBUS/OPCUA/LV2/SEQUENT) live only in the
      // binary CDO — include them so editing an action can re-select any
      // command the adapter actually defines (mirrors handleBankChange's
      // Source 4).
      (
        await fetchBinaryCdoControlEntries(
          resolveProtocolKey(wizardState.adapterProtocolByBank?.[bankId], bankData?.adapter),
          adapterId,
          token,
        )
      ).forEach((e) => controlValues.add(e.enumValue));
      const keysRaw: unknown = (keysResult.data as any)?.data ?? keysResult.data ?? [];
      const adapterKeys: any[] = Array.isArray(keysRaw) ? keysRaw : [];
      adapterKeys.forEach((k: any) => {
        if (k.messageContent === MessageContent.Control_ControlMessage) {
          controlValues.add(k.messageValue);
        }
      });
      const filtered = ALL_CONTROL_OPTIONS.filter((opt) => controlValues.has(opt.numeric));
      editCommandOptionsCacheRef.current.set(bankId, filtered);
      setEditCommandOptions(filtered);
    } catch {
      // Falls back to ALL_CONTROL_OPTIONS so the operator can still pick.
      setEditCommandOptions(ALL_CONTROL_OPTIONS);
    }
  };

  const closeEditModal = () => {
    setEditingRow(null);
    setEditLabel("");
    setEditMessageType(undefined);
    setEditButtonType("SINGLE");
    setEditTimer(0);
    setEditToggleTimer(0);
    setEditToggleMessageType(undefined);
    setEditToggleLabel("");
    setEditLiftId(undefined);
    setEditCommandOptions([]);
  };

  /**
   * Persist edits via usePutActionMutation. The PUT body mirrors the
   * POST contract (snake_case). Behavior fields that don't apply to the
   * chosen button type are sent as EXPLICIT zero-values ("" / 0) — the
   * backend's map-based update writes provided values verbatim, so the
   * explicit zeros clear stale config when switching types (no ghost
   * toggle_cmd surviving a TOGGLE → Single Click change).
   */
  const handleSaveEdit = async () => {
    if (!editingRow) return;
    if (!editLabel.trim() || !editMessageType) return;
    // The row carries its own bankId — no lookup by display name needed.
    const bankId = editingRow.bankId;
    if (!bankId) return;
    const token = await GETJwtToken();
    const liftType = (wizardState.bankTypes?.[bankId] as string) || "";
    const buttonTypeString = wizardButtonTypeToString(editButtonType);

    const usesToggle = usesToggleField(editButtonType);
    const usesTimer = usesTimerField(editButtonType);
    const persistedTimer = usesTimer && editTimer > 0 ? editTimer : 0;
    const persistedToggleTimer =
      needsToggleTimer(editButtonType) && editToggleTimer > 0 ? editToggleTimer : 0;
    const persistedToggleCmd = usesToggle && editToggleMessageType ? editToggleMessageType : "";
    const persistedToggleLabel =
      usesToggle && persistedToggleCmd && editToggleLabel.trim() ? editToggleLabel.trim() : "";

    const numericForEditCommand = (cmd: string): number | undefined =>
      resolveNumericForCommand(editCommandOptions, cmd);
    // Falls back to the generated Control enum, matching the edit
    // dropdowns themselves — they render ALL_CONTROL_OPTIONS whenever the
    // row's bank yielded no adapter options, so resolving against
    // editCommandOptions alone would drop `command` for exactly the
    // commands the operator could still pick.
    const commandObjectForEditCommand = (cmd: string): ControlOption | undefined =>
      resolveControlOptionForCommand(editCommandOptions, cmd) ??
      deriveControlOption(cmd, undefined);

    const body: Record<string, unknown> = {
      label: editLabel.trim(),
      cmd: editMessageType,
      lift_type: liftType,
      target_position: 0,
      valid_states: [],
      bank_id: bankId,
      org_id: wizardState.organizationId,
      button_type: buttonTypeString,
      timer: persistedTimer,
      toggle_timer: persistedToggleTimer,
      toggle_cmd: persistedToggleCmd,
      toggle_label: persistedToggleLabel,
    };
    const editMessageValue = numericForEditCommand(editMessageType);
    if (editMessageValue !== undefined) body.message_value = editMessageValue;
    const editCommandObject = commandObjectForEditCommand(editMessageType);
    if (editCommandObject) body.command = editCommandObject;
    const editToggleCommandObject = persistedToggleCmd
      ? commandObjectForEditCommand(persistedToggleCmd)
      : undefined;
    // ALWAYS sent, `null` when there is no toggle command — same
    // explicit-zero-value contract as toggle_cmd/toggle_label above. The
    // backend's map-based update writes provided values verbatim, so
    // omitting it on a TOGGLE → Single Click switch would leave the old
    // toggle_command object stranded on the row.
    body.toggle_command = editToggleCommandObject ?? null;
    if (persistedToggleCmd) {
      const editToggleValue = numericForEditCommand(persistedToggleCmd);
      if (editToggleValue !== undefined) body.toggle_message_value = editToggleValue;
    }
    if (editLiftId) body.lift_id = editLiftId;

    try {
      await triggerPutAction({ id: editingRow.id, body, token }).unwrap();
      closeEditModal();
      // Re-read the bank instead of patching the row from the request body:
      // what the table shows is what the server stored, so a field the
      // backend normalised or refused is visible immediately rather than
      // after the next mount.
      await refreshBankRows(bankId);
    } catch (err: any) {
      setStepError(`Failed to update action — ${describeError(err)}`);
    }
  };

  /**
   * Open the Visual Config modal pre-filled from the matching
   * action_bank row. The row must already exist server-side (the
   * SeedBankControlActions reconciler creates one per action_catalog);
   * the table's enable-check on the Visual button guarantees we never
   * open the modal for a row without a matching record.
   */
  const openVisualModal = (row: ActionRow) => {
    const ab = actionBankMap.get(row.id);
    if (!ab) return;
    setEditingVisualRowId(row.id);
    setVisualError(null);
    // ActionBankT fields are typed; string|Uint8Array|null for text and
    // number-with-defaults for timers per the generated Object API. The
    // `String(... ?? "")` coercion normalises Uint8Array | null → "" so
    // the inputs always receive a real string.
    setVisualColor(typeof ab.color === "string" ? ab.color : "");
    setVisualColor2(typeof ab.color2 === "string" ? ab.color2 : "");
    setVisualToggleColor(typeof ab.toggleColor === "string" ? ab.toggleColor : "");
    setVisualTimer(ab.timer ?? 0);
    setVisualTimer2(ab.timer2 ?? 0);
    setVisualToggleTimer(ab.toggleTimer ?? 0);
    setVisualValidStates(ab.validStates ?? []);
    setVisualToggleValidStates(ab.toggleValidStates ?? []);
    setVisualLabel(typeof ab.label === "string" ? ab.label : "");
    setVisualToggleLabel(typeof ab.toggleLabel === "string" ? ab.toggleLabel : "");
    setVisualModalOpen(true);
  };

  /** Returns true when all three color fields are either empty or valid #RRGGBB. */
  const visualColorsValid = (): boolean =>
    validateHexColor(visualColor) === null &&
    validateHexColor(visualColor2) === null &&
    validateHexColor(visualToggleColor) === null;

  /**
   * PUT the action_bank's visual config. Uses the composite key
   * (messageId, bankId) from the looked-up record — both come from the
   * server response so neither requires UUID parsing on the client.
   * On success, refresh the map entry so subsequent opens see the
   * latest values without a second roundtrip.
   */
  const handleSaveVisual = async () => {
    if (!editingVisualRowId) return;
    if (!visualColorsValid()) {
      setVisualError("Fix the color field errors before saving.");
      return;
    }
    const ab = actionBankMap.get(editingVisualRowId);
    if (!ab) {
      setVisualError("Action_bank record disappeared — close and reopen.");
      return;
    }
    setVisualSaving(true);
    setVisualError(null);
    try {
      const token = await GETJwtToken();
      // ActionBankT is typed camelCase against the
      // flattened-valued-messages schemas — read fields directly off the
      // Object API instance, no snake_case fallback.
      const messageId = formatUuidT(ab.messageId);
      const bankId = formatUuidT(ab.bankId);
      const organizationId = formatUuidT(ab.organizationId) || wizardState.organizationId;
      const actionId = formatUuidT(ab.actionId);
      const toggleActionId = formatUuidT(ab.toggleActionId);
      const toggleMessageId = formatUuidT(ab.toggleMessageId);
      // The wire body still uses snake_case JSON keys because the
      // UpdateActionBank serializer's str()/uuidField()/num() lookups
      // pull from snake_case — that's our boundary between the typed
      // FB world (camelCase) and the JSON-named HTTP payload (snake).
      // The serializer then packs into an ActionBankT and the LN2E
      // envelope wraps the FB bytes on the way to the server.
      await triggerPutActionBank({
        messageId,
        bankId,
        token,
        body: {
          organization_id: organizationId,
          action_id: actionId || undefined,
          toggle_action_id: toggleActionId || undefined,
          toggle_message_id: toggleMessageId || undefined,
          button_type: ActionButtonType[ab.buttonType] ?? "TOGGLE",
          label: visualLabel,
          toggle_label: visualToggleLabel,
          color: visualColor,
          color2: visualColor2,
          toggle_color: visualToggleColor,
          timer: visualTimer,
          timer2: visualTimer2,
          toggle_timer: visualToggleTimer,
          valid_states: visualValidStates,
          toggle_valid_states: visualToggleValidStates,
        },
      }).unwrap();
      // Update the local map with a properly-mutated ActionBankT so
      // subsequent opens see the edited values. We mutate-then-clone
      // (Object.assign onto a fresh instance) rather than spread to
      // preserve the ActionBankT prototype the typing expects.
      const updated = new ActionBankT(
        ab.messageId,
        ab.bankId,
        ab.organizationId,
        ab.actionId,
        ab.toggleActionId,
        ab.toggleMessageId,
        ab.buttonType,
        visualLabel,
        visualToggleLabel,
        visualValidStates,
        visualToggleValidStates,
        visualColor,
        visualColor2,
        visualToggleColor,
        visualTimer,
        visualTimer2,
        visualToggleTimer,
        ab.createdAt,
        ab.updatedAt,
      );
      setActionBankMap((prev) => {
        const next = new Map(prev);
        next.set(editingVisualRowId, updated);
        return next;
      });
      setVisualModalOpen(false);
      setEditingVisualRowId(null);
    } catch (err: unknown) {
      setVisualError(`Failed to save visual config — ${describeError(err)}`);
    } finally {
      setVisualSaving(false);
    }
  };

  /**
   * Delete a single action via the DELETE endpoint, then re-read the bank so
   * the table reflects what the server holds afterwards rather than an
   * optimistic guess. The wizardState.actionCatalogIds entry is pruned too, so
   * the wizard-completion gate stays accurate.
   */
  const handleDeleteRow = async (row: ActionRow) => {
    setDeletingId(row.id);
    try {
      const token = await GETJwtToken();
      await triggerDeleteAction({ id: row.id, token }).unwrap();
      setWizardState((prev) => ({
        ...prev,
        actionCatalogIds: pruneActionCatalogId(prev.actionCatalogIds, row.id),
      }));
      await refreshBankRows(row.bankId);
    } catch (err: any) {
      setStepError(`Failed to delete action — ${describeError(err)}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Determine the options and placeholder for the command select.
  // MCE adapters expose no per-key control mappings (they map messages
  // internally in VTS), so the adapter-derived list is empty. For MCE banks
  // fall back to the full Control catalog so operators can add any control
  // command rather than seeing "No control commands in this adapter".
  const commandOptions = resolveManualCommandOptions({
    isMceBank,
    isModbusBank,
    adapterControlOptions,
    // With no single bank selected there is no single adapter to derive a list
    // from, and one bank's adapter cannot speak for the rest — the catalog is
    // the only list that means the same thing for every target.
    showAllCommands: showAllCommands || addToAllBanks,
  });
  // The operator asked for the full catalog on a bank whose adapter did report
  // commands, so the list now mixes adapter-verified and catalog-only entries.
  const manualCatalogOverrideActive = isManualCatalogOverrideActive({
    isMceBank,
    isModbusBank,
    adapterControlOptions,
    showAllCommands,
  });
  // MODBUS banks whose binary CDO decoded to zero control writes fall back to
  // the full catalog (see resolveManualCommandOptions) — surface that so the
  // operator knows these are catalog commands, not adapter-verified writes.
  const modbusCatalogFallbackActive = isModbusCatalogFallbackActive({
    isMceBank,
    isModbusBank,
    adapterControlOptions,
  });
  /**
   * Rows on the selected bank whose command its adapter no longer exposes.
   *
   * Only the selected bank can be judged: adapterControlOptions is loaded for
   * one bank at a time, and a command absent from THIS adapter says nothing
   * about a row belonging to another. Recomputed from state the step already
   * holds, so nothing is fetched for it.
   */
  const orphanRowIds = useMemo(() => {
    if (!selectedBankId || addToAllBanks) return new Set<string>();
    return new Set(
      orphanedActionIds(actions, selectedBankId, exposedCommandNames(adapterControlOptions)),
    );
  }, [actions, selectedBankId, addToAllBanks, adapterControlOptions]);

  const hasAdapterKeys = isMceBank || addToAllBanks || adapterControlOptions !== undefined;
  const noControlKeys = hasAdapterKeys && commandOptions.length === 0;
  // SINGLE has no optional capabilities at all (see BUTTON_TYPE_CAPS), so
  // these are false for it and true for SWITCH (its optional off-sequence
  // preset) and every mandatory-toggle/timer type — no special-casing by
  // button type needed here now that the two presets are distinct
  // WizardButtonType values.
  const showToggleActionSection = usesToggleField(bankAction.buttonType);

  /**
   * Build a ControlOption[] (catalog shape: value=number) from the
   * configured ActionRows so the Scheduled Actions stage can offer
   * exactly the buttons an operator has already set up, rather than the
   * full Control enum catalog. De-duplicated by numeric value — the first
   * row encountered for a given value wins (preserves operator label).
   * Falls back to an empty array; WizardScheduledActions applies its own
   * buildControlOptions() fallback when this is empty.
   */
  const scheduledControlOptions = useMemo<ScheduledControlOption[]>(() => {
    const seen = new Set<number>();
    const opts: ScheduledControlOption[] = [];
    actions.forEach((row) => {
      const numVal = resolveNumericForCommand(commandOptions, row.messageType);
      if (numVal !== undefined && !seen.has(numVal)) {
        seen.add(numVal);
        const toggleNumVal = row.toggleMessageType
          ? resolveNumericForCommand(commandOptions, row.toggleMessageType)
          : undefined;
        opts.push({
          value: numVal,
          label: row.label || row.messageType,
          defaultStopValue: toggleNumVal,
        });
      }
    });
    return opts;
  }, [actions, commandOptions]);

  const getCommandPlaceholder = () => {
    if (addToAllBanks) return "Select control command";
    if (!selectedBankId) return "Select a bank first";
    if (!hasAdapterKeys) return "Loading...";
    if (noControlKeys) return "No control commands in this adapter";
    return "Select control command";
  };

  const getToggleCommandPlaceholder = () => {
    if (mirrorsToggleCommand(bankAction.buttonType)) return "Same as the action command";
    if (!selectedBankId && !addToAllBanks) return "Select a bank first";
    if (needsToggle(bankAction.buttonType)) return "Select off-state command";
    return "Select off-state command (optional)";
  };

  // ************************************************ */
  // USE EFFECTS *********************************** */

  // Load the actions table from the server on entering this step.
  //
  // The ONE source is `/action-banks/by-bank/:bankId`, one request per bank —
  // the same endpoint the monitoring control panels read through
  // useBankActions, so the wizard and monitoring describe the same rows from
  // the same table. The table is a projection of those responses and nothing
  // else: what the endpoint returns for a bank is exactly what that bank's
  // table shows.
  //
  // It used to be `/action-catalogs` filtered by `wizardState.actionCatalogIds`,
  // which could not agree with the endpoint in either direction:
  //
  //  - an action_bank row references TWO catalog rows (`action_id` and
  //    `toggle_action_id`) and they are frequently distinct records, so a bank
  //    with two configured buttons listed three rows here and two everywhere
  //    else; the extra row was one half of a button, with no action_bank of
  //    its own, so its Visual button was dead and its Color columns empty.
  //  - a catalog row that no action_bank references at all — left behind by an
  //    earlier seed — still listed here, which is how a bank whose endpoint
  //    answers 2 rows showed 10.
  //
  // The dependency is a STRING key of the bank list, and the effect has no
  // cleanup that discards an in-flight answer. Both matter. WizardContext
  // rebuilds `markStepCompleted` (and every other callback on its value) on
  // each render, so an effect that listed it — or that listed the wizardState
  // objects — re-ran on every wizard state change. With a `cancelled` flag in
  // the cleanup, each re-run threw away the response that was still in flight
  // and the bank never got marked as loaded, so the next pass asked again and
  // was cancelled in turn: the requests all returned 200 with rows, and the
  // table stayed empty at 0 for every bank. Keying on the bank list means the
  // effect re-runs only when the list of banks actually changes, and dropping
  // the cancel means a response that arrives is always applied.
  const bankIdsKey = wizardState.bankIds.join("|");
  useEffect(() => {
    // Only the banks we have neither read nor asked for. On the first pass
    // that is all of them; later it is exactly the new arrivals, which is what
    // lets a bank that reaches wizardState.bankIds late still get loaded.
    const pendingBankIds = wizardState.bankIds.filter(
      (id) => !loadedBanksRef.current.has(id) && !loadingBanksRef.current.has(id),
    );
    if (pendingBankIds.length === 0) return;
    pendingBankIds.forEach((id) => loadingBanksRef.current.add(id));

    (async () => {
      try {
        const token = await GETJwtToken();
        const perBank = await loadActionBankRowsForBanks(pendingBankIds, token);

        // A bank whose request failed is left OUT of loadedBanksRef so a later
        // pass retries it: "we could not ask" must not settle as "it is empty".
        const loaded = perBank.filter((entry) => !entry.failed);
        const rows: ActionRow[] = loaded.flatMap((entry) =>
          actionRowsFromActionBanks(entry.rows, bankRowContext(entry.bankId)),
        );

        // Mark loaded only AFTER the async chain produced a real result.
        // Marking pre-await meant a failed GETJwtToken (auth expired) locked
        // the operator out of any further load attempt for this mount.
        loaded.forEach((entry) => loadedBanksRef.current.add(entry.bankId));

        // MERGE, never replace: the banks answered here are the pending ones,
        // so a bank loaded on an earlier pass must keep its rows.
        const answered = new Set(loaded.map((entry) => entry.bankId));
        setActions((prev) => [...prev.filter((row) => !answered.has(row.bankId)), ...rows]);
        if (rows.length > 0) markStepCompletedRef.current(WizardStep.Actions);
      } catch (err: any) {
        setStepError(`Failed to load actions — ${describeError(err)}`);
      } finally {
        pendingBankIds.forEach((id) => loadingBanksRef.current.delete(id));
      }
    })();
  }, [bankIdsKey]);

  useEffect(() => {
    if (isSuccessPost && dataPost) {
      const actionData = dataPost.data || dataPost;
      const actionId = formatUuidT(actionData.id) || String(actionData.id || "");
      if (!actionId) return;

      const bankId = selectedBankId || "";

      setWizardState((prev) => {
        const updatedIds = structuredClone(prev.actionCatalogIds);
        updatedIds.push(actionId);
        return { ...prev, actionCatalogIds: updatedIds };
      });
      markStepCompleted(WizardStep.Actions);

      // Claim this as the add whose verification result gets applied. A
      // second add started before this one's refreshBankRows resolves
      // reassigns the ref to ITS actionId, so this closure's write below is
      // skipped when it finally resolves.
      latestPendingActionIdRef.current = actionId;

      // The created row reaches the table by re-reading the bank, not by being
      // reconstructed from the form: the endpoint is the only thing that
      // decides what this bank contains.
      //
      // That trust is why this has to verify. A create can return 201 having
      // written the catalog row but not the action_bank row that renders it —
      // the server keeps the catalog deliberately — and re-reading the bank
      // then shows nothing new. Clearing the form on that would wipe the
      // operator's input and leave them staring at an unchanged table with no
      // error: the exact failure this step used to have.
      (async () => {
        const status = readMaterializationStatus(dataPost);
        const fresh = await refreshBankRows(bankId);

        // A later add's verification claimed the ref while this one was in
        // flight — its result is stale, so it must not clobber the newer
        // add's toast, error banner, or form state.
        if (latestPendingActionIdRef.current !== actionId) return;

        const landed = fresh === null ? undefined : fresh.some((row) => row.id === actionId);

        // The server's verdict wins when it gave one. Otherwise fall back to
        // whether the row came back — which is all an older server supports.
        // A refresh that itself failed (null) proves nothing either way, so it
        // is not treated as a missing row.
        const rendered = status ? willRender(status) : landed !== false;
        setVerifiedActionSuccess(rendered);
        if (!rendered) {
          setStepError(materializationWarning(wizardState.bankNames?.[bankId] || "", status));
          return;
        }

        clearForm();
      })();
    }
  }, [isSuccessPost, dataPost]);

  // ************************************************ */
  // TABLE COLUMNS ********************************* */
  /**
   * Render the actions table \u2014 position column is gone because the wizard
   * now creates a single catalog row per (cmd \u00d7 lift) with target_position
   * always 0; the floor is filled in by the monitoring floor-button strip
   * at command time. The Button Type column shows the operator's selected
   * behaviour for downstream verification. Edit + Delete live in a final
   * Actions column so the operator can CRUD any auto-generated row.
   *
   * The Bank column is intentionally absent: the grouped renderer below
   * wraps each per-bank Table with a section header showing the bank
   * name, so per-row repetition would be visual noise.
   */
  /**
   * The Lift cell: which unit this action addresses.
   *
   * Reads `actionScope`, not just the lift name. The name answers "which car
   * did the wizard pin this to", which is a different question from "can this
   * command address a single car at all" — and only the second decides what the
   * cell should say.
   *
   * Before this, an empty name printed "Bank-Level" unconditionally, so every
   * unpinned row claimed to be bank-wide. That was wrong for exactly the rows
   * an operator most needs it to be right for: Independent Service, Secure Car
   * Call and Energize Car Call all come back UNIT-scoped, and monitoring
   * already routes them to the per-car drawer. The wizard and the dashboard
   * disagreed about the same row, and the wizard was the one that was wrong.
   *
   * UNKNOWN keeps the old label. The server sends it when the command's key
   * cannot be found, and inventing a scope from a row that says it does not
   * know one would replace a visible gap with a confident wrong answer.
   */
  function renderLiftName(val: string, row: ActionRow): React.ReactNode {
    if (val) return val;

    if (row.actionScope === ActionScope.UNIT) {
      return (
        <Tooltip title="This command addresses one car. It is not pinned to a specific lift, so monitoring offers it on every car in the bank.">
          <Tag color="blue">{ALL_CARS_LIFT_LABEL}</Tag>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        title={
          row.actionScope === ActionScope.BANK
            ? "This command is bank-wide — the vendor encodes it once for the whole group, so there is no single car to send it to."
            : "The server could not determine this command's scope from the adapter key, so the bank-wide default is shown."
        }
      >
        <Tag color="purple">{BANK_LEVEL_LIFT_LABEL}</Tag>
      </Tooltip>
    );
  }

  // Memoize the actionColumns so AntD's Table column-identity check
  // doesn't fire a full re-render every parent state change. Deps
  // capture the handlers and the per-row loading indicator.
  const actionColumns = useMemo(
    () => [
      // Column order mirrors the action_catalog row shape (bank_id, org_id,
      // lift_type, button_type, target_position, cmd, message_value, label,
      // tooltip, color, valid_states, toggle_cmd, toggle_message_value,
      // toggle_label, toggle_tooltip, toggle_color, toggle_valid_states),
      // with Lift/Timer/Toggle Timer slotted in next to their related
      // fields since they have no DB column of their own.
      {
        title: columnTitle("Bank ID"),
        dataIndex: "bankId",
        key: "bankIdColumn",
        render: (val?: string) => renderCopyableId(val, NO_BANK_ID_REASON),
      },
      {
        title: columnTitle("Org ID"),
        dataIndex: "orgId",
        key: "orgId",
        render: (val?: string) => renderCopyableId(val, NO_ORG_ID_REASON),
      },
      {
        title: columnTitle("Lift Type"),
        dataIndex: "liftType",
        key: "liftType",
        render: (val?: string) => val || renderNotApplicable(NO_LIFT_TYPE_REASON),
      },
      {
        title: columnTitle("Lift"),
        dataIndex: "liftName",
        key: "liftName",
        render: renderLiftName,
      },
      {
        title: columnTitle("Button Type"),
        dataIndex: "buttonType",
        key: "buttonType",
        // Reuse the canonical Bank-Action Tag renderer so wizard and
        // dedicated Bank Actions screen stay visually aligned (also
        // gets free coverage for HOLDBACK/TURNBACK/ENERGIZER_TURN_BACK
        // if a row was promoted to one of those variants out-of-band).
        render: (val: string) => <TableColumnBankActionType actionType={val} />,
      },
      {
        title: columnTitle("Command"),
        dataIndex: "messageType",
        key: "messageType",
        render: (val: string, row: ActionRow) => {
          // An unbound primary is the one blank on this row that is a DEFECT,
          // so it borrows the runtime's own sentence rather than inventing a
          // second account of the same condition.
          if (!val) return renderUnconfigured(UNBOUND_ACTION_REASON);
          if (!orphanRowIds.has(row.id)) return `Control::${val}`;
          // Flagged rather than removed: the row may be an operator's
          // deliberate choice, and the server never prunes those. Deleting it
          // for them would be the competing-reconciler mistake all over again.
          return (
            <Tooltip title={ORPHAN_TOOLTIP}>
              <span>
                {`Control::${val}`}{" "}
                <Tag color="orange" data-testid="orphan-flag">
                  Not on adapter
                </Tag>
              </span>
            </Tooltip>
          );
        },
      },
      // The three halves of the `command` object the operator picked from
      // the Control Command dropdown, broken out so each is sortable /
      // scannable on its own rather than buried in one JSON-ish cell.
      // Rows created before `command` existed (and any the server echoes back
      // without it) recover each half from `messageType` / `messageValue` — the
      // same binding under its older spelling — and mark it as derived. Only a
      // row with no binding at all reports the command unset.
      {
        title: columnTitle("Command Label"),
        dataIndex: ["command", "label"],
        key: "commandLabel",
        render: (val: string | undefined, row: ActionRow) => {
          if (val) return val;
          // A row without the `command` object is not a row without a command:
          // `messageType` is the same binding under its older spelling, so the
          // answer is recoverable and printing it beats printing a gap.
          if (row.messageType)
            return renderDefaulted(formatCamelCaseLabel(row.messageType), DERIVED_COMMAND_REASON);
          return renderUnconfigured(UNBOUND_ACTION_REASON);
        },
      },
      {
        title: columnTitle("Command Value"),
        dataIndex: ["command", "value"],
        key: "commandValue",
        render: (val: string | undefined, row: ActionRow) => {
          if (val) return val;
          if (row.messageType) return renderDefaulted(row.messageType, DERIVED_COMMAND_REASON);
          return renderUnconfigured(UNBOUND_ACTION_REASON);
        },
      },
      {
        title: columnTitle("Command Numeric"),
        dataIndex: ["command", "numeric"],
        key: "commandNumeric",
        // `typeof === "number"` and never truthiness: Control.CarCallLock is a
        // legitimate 0, and a falsy check would blank exactly that command.
        render: (val: number | undefined, row: ActionRow) => {
          if (typeof val === "number") return val;
          if (typeof row.messageValue === "number")
            return renderDefaulted(row.messageValue, DERIVED_COMMAND_REASON);
          return renderUnconfigured(UNBOUND_ACTION_REASON);
        },
      },
      {
        title: columnTitle("Message Value"),
        dataIndex: "messageValue",
        key: "messageValue",
        render: (val: number | undefined, row: ActionRow) => {
          if (typeof val === "number") return val;
          if (typeof row.command?.numeric === "number")
            return renderDefaulted(row.command.numeric, DERIVED_COMMAND_REASON);
          return renderUnconfigured(UNBOUND_ACTION_REASON);
        },
      },
      {
        title: columnTitle("Label"),
        dataIndex: "label",
        key: "label",
        // Previously the only column with no renderer at all, so an unlabelled
        // action produced a cell that was not even an em dash — just empty.
        render: (val?: string) => val || renderUnconfigured(NO_LABEL_REASON),
      },
      {
        // color/toggle_color/timer/toggle_timer/toggle_valid_states live
        // ONLY on the action_bank record, never on action_catalog (the
        // `actions` state's source) — fall back to actionBankMap (keyed
        // by action_id === row.id) whenever the row itself doesn't carry
        // the field, so a hydrated/bulk-created row still shows its real
        // server-assigned value rather than falling through to the default.
        title: columnTitle("Color"),
        dataIndex: "color",
        key: "color",
        render: (val: string | undefined, row: ActionRow) =>
          renderColorCell(
            val ?? abTextField(actionBankMap.get(row.id)?.color),
            DEFAULT_COLOR_REASON,
          ),
      },
      {
        title: columnTitle("Valid States"),
        dataIndex: "validStates",
        key: "validStates",
        render: (val?: string[]) => renderValidStatesCell(val, NO_VALID_STATES_REASON),
      },
      {
        title: columnTitle("Toggle Command"),
        dataIndex: "toggleMessageType",
        key: "toggleMessageTypeColumn",
        render: (val: string | undefined, row: ActionRow) =>
          renderPairedCommandCell(row, () =>
            val ? `Control::${val}` : renderUnconfigured(UNBOUND_COMMAND_REASON),
          ),
      },
      {
        title: columnTitle("Toggle Message Value"),
        dataIndex: "toggleMessageValue",
        key: "toggleMessageValue",
        render: (val: number | undefined, row: ActionRow) =>
          renderPairedCommandCell(row, () =>
            typeof val === "number" ? val : renderUnconfigured(UNBOUND_COMMAND_REASON),
          ),
      },
      {
        title: columnTitle("Toggle Label"),
        dataIndex: "toggleLabel",
        key: "toggleLabelColumn",
        render: (val: string | undefined, row: ActionRow) =>
          renderSecondButtonCell(row, () => val || renderUnconfigured(NO_TOGGLE_LABEL_REASON)),
      },
      {
        title: columnTitle("Toggle Color"),
        dataIndex: "toggleColor",
        key: "toggleColor",
        render: (val: string | undefined, row: ActionRow) =>
          renderSecondButtonCell(row, () => {
            const ab = actionBankMap.get(row.id);
            return renderColorCell(
              val ?? abTextField(abFallbackField<string>(ab, "toggleColor", "toggle_color")),
              DEFAULT_TOGGLE_COLOR_REASON,
            );
          }),
      },
      {
        title: columnTitle("Toggle Valid States"),
        dataIndex: "toggleValidStates",
        key: "toggleValidStates",
        render: (val: string[] | undefined, row: ActionRow) =>
          renderSecondButtonCell(row, () => {
            const ab = actionBankMap.get(row.id);
            const fallback = abFallbackField<string[]>(
              ab,
              "toggleValidStates",
              "toggle_valid_states",
            );
            return renderValidStatesCell(
              val ?? (fallback && fallback.length > 0 ? fallback : undefined),
              NO_TOGGLE_VALID_STATES_REASON,
            );
          }),
      },
      {
        title: columnTitle("Timer (s)"),
        dataIndex: "timer",
        key: "timerColumn",
        render: (val: number | undefined, row: ActionRow) =>
          renderTimerCell(
            row,
            positiveDuration(val) ?? positiveDuration(actionBankMap.get(row.id)?.timer),
          ),
      },
      {
        title: columnTitle("Toggle Timer (s)"),
        dataIndex: "toggleTimer",
        key: "toggleTimerColumn",
        render: (val: number | undefined, row: ActionRow) =>
          renderToggleTimerCell(
            row,
            positiveDuration(val) ??
              positiveDuration(
                abFallbackField<number>(actionBankMap.get(row.id), "toggleTimer", "toggle_timer"),
              ),
          ),
      },
      {
        title: columnTitle("Actions"),
        fixed: "right" as const,
        key: "rowActions",
        width: 180,
        render: (_: unknown, row: ActionRow) => {
          const hasActionBank = actionBankMap.has(row.id);
          return (
            <Space size="small">
              <WizardOpGate op="update">
                <Tooltip title="Edit action">
                  <Button
                    size="small"
                    aria-label={`Edit action ${row.label}`}
                    onClick={() => openEditModal(row)}
                    icon={<FontAwesomeIcon icon={faPenToSquare} />}
                  />
                </Tooltip>
              </WizardOpGate>
              <WizardOpGate op="update">
                <Tooltip
                  title={
                    hasActionBank
                      ? `Edit visual config (color, valid states, timers) for ${row.label}`
                      : "Visual config requires running reconciler; available after server-side seed"
                  }
                >
                  {/* Wrap in span so the disabled Button still fires a Tooltip */}
                  <span>
                    <Button
                      size="small"
                      aria-label={`Visual config for ${row.label}`}
                      onClick={() => openVisualModal(row)}
                      disabled={!hasActionBank}
                      icon={<FontAwesomeIcon icon={faEye} />}
                    />
                  </span>
                </Tooltip>
              </WizardOpGate>
              <WizardOpGate op="delete">
                <Popconfirm
                  title="Delete this action? This permanently removes the action catalog entry."
                  okText="Delete"
                  okType="danger"
                  cancelText="Cancel"
                  onConfirm={() => handleDeleteRow(row)}
                >
                  {/* Popconfirm renders its own affordance prompt, so a
                    redundant Tooltip wrapper on the trigger button just
                    competes with the popover. aria-label carries the
                    accessible name. */}
                  <Button
                    size="small"
                    danger
                    aria-label={`Delete action ${row.label}`}
                    loading={deletingId === row.id}
                    icon={<FontAwesomeIcon icon={faTrashCan} />}
                  />
                </Popconfirm>
              </WizardOpGate>
            </Space>
          );
        },
      },
    ],
    [deletingId, openEditModal, handleDeleteRow, openVisualModal, actionBankMap, orphanRowIds],
  );

  /**
   * One entry per bank the wizard knows about, in wizard order, each carrying
   * that bank's rows grouped by lift.
   *
   * Driven by `wizardState.bankIds` rather than by the rows themselves, so a
   * bank with NO actions still gets its own collapse (with a 0 tag) instead of
   * vanishing. A bank that has no section is indistinguishable from a bank
   * that failed to load — the operator cannot tell "this bank has nothing
   * configured" from "the wizard never asked", and the second is a bug they
   * need to see.
   *
   * Banks are keyed by id, not by display name: two banks may share a name,
   * and grouping those together would merge one bank's actions into another's
   * table. Any bankId that shows up in rows but not in the wizard's list is
   * appended at the end rather than dropped.
   *
   * Memoized so we don't re-group and re-sort on every unrelated state update
   * (Modal keystroke, hover, etc.).
   */
  const bankSections = useMemo(() => {
    const orderedBankIds = [...wizardState.bankIds];
    const known = new Set(orderedBankIds);
    actions.forEach((row) => {
      if (row.bankId && !known.has(row.bankId)) {
        known.add(row.bankId);
        orderedBankIds.push(row.bankId);
      }
    });
    return orderedBankIds.map((bankId) => {
      // Reuse the tested lift grouping on the bank's own slice; with a single
      // bank in it, the helper returns at most one bank group. Flattening it
      // back out keeps the helper's ordering — Bank-Level lift group first,
      // then lifts alphabetically, rows by label within each.
      const [group] = groupActionsByBankAndLift(actions.filter((row) => row.bankId === bankId));
      const liftGroups = group?.liftGroups ?? [];
      return {
        bankId,
        bankName: wizardState.bankNames?.[bankId] || "",
        rows: liftGroups.flatMap((liftGroup) => liftGroup.rows),
      };
    });
  }, [actions, wizardState.bankIds, wizardState.bankNames]);

  // Lift options for the Edit modal — driven by the edited row's bankId.
  // Memoized so typing in the modal's other fields doesn't rebuild the
  // [liftId, liftName] tuple list on every keystroke.
  /**
   * Button Type choices for the Edit modal.
   *
   * Single Click is no longer offered — new actions are Toggle by default — but
   * rows created before that, and rows whose stored type the wizard never
   * surfaced, still have to be editable. Without their own entry the Select
   * would render blank and the first save would silently retype the row, so a
   * stored type that is not in the list is appended as a legacy choice.
   */
  const editButtonTypeOptions = useMemo(() => {
    if (WIZARD_BUTTON_TYPE_OPTIONS.some((opt) => opt.value === editButtonType)) {
      return WIZARD_BUTTON_TYPE_OPTIONS;
    }
    return [
      ...WIZARD_BUTTON_TYPE_OPTIONS,
      {
        value: editButtonType,
        label: `${editButtonType} (legacy)`,
        description: "This row's stored button type is no longer offered for new actions.",
      },
    ];
  }, [editButtonType]);

  const editLiftOptions = useMemo<Array<[string, string]>>(() => {
    if (!editingRow?.bankId) return [];
    const liftMap = wizardState.liftNamesByBank?.[editingRow.bankId] ?? {};
    return Object.entries(liftMap);
  }, [editingRow, wizardState.liftNamesByBank]);

  // ************************************************ */
  // COMPONENT ************************************* */
  return (
    <Spin spinning={isLoadingPost || isAddingToAllBanks}>
      <DrawerRowDivider icon={faListCheck} title="Actions" />
      <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
        {/* ACTIONS TABLE — grouped by Bank, then by Lift within each bank.
            Each bank renders its own AntD Table; the Lift column inside
            the per-bank table shows the lift name, with "Bank-Level"
            (purple Tag) for entries that have no lift_id (hall calls,
            etc.) This matches the operator's mental model of "what
            buttons will appear when I click this bank on the dashboard
            vs. when I click a specific car". */}
        {bankSections.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div>
              {bankSections.map((bank) => (
                <div key={bank.bankId} className="row mt-3">
                  <div className="col-12">
                    {/* Collapsed by default (no defaultActiveKey) so the
                        full per-bank action list isn't dumped on screen
                        the moment the step loads — the operator expands
                        only the banks they need. */}
                    <Collapse>
                      <Collapse.Panel
                        header={
                          <span
                            style={{
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span style={{ width: "200px" }}>
                              {" "}
                              {bank.bankName || "Unassigned Bank"}
                            </span>
                            {/* Always rendered, 0 included — the count IS the
                                answer the endpoint gave for this bank. */}
                            <Tag color={bank.rows.length > 0 ? "geekblue" : "default"}>
                              {bank.rows.length}
                            </Tag>
                          </span>
                        }
                        key="actions"
                        // Keep the table mounted (just visually hidden) while
                        // collapsed, rather than the default lazy-mount —
                        // Edit/Delete row actions and other logic that looks
                        // up rows by role/label need the DOM present.
                        forceRender
                      >
                        <Table
                          dataSource={bank.rows}
                          columns={actionColumns}
                          pagination={false}
                          size="small"
                          rowKey="key"
                          locale={{ emptyText: "No actions for this bank" }}
                          // Many detail columns now — scroll horizontally
                          // rather than squeezing/wrapping every column,
                          // and keep the Edit/Visual/Delete Actions
                          // column reachable via `fixed: "right"` above.
                          scroll={{ x: "max-content" }}
                        />
                      </Collapse.Panel>
                    </Collapse>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <br />
      <DrawerRowDivider icon={faUpDown} title="Add Manual Action" />
      <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
        <div className="container">
          {/* Row 1 — Bank + the all-banks switch, half the row each. */}
          <div className="row">
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">
                Bank{" "}
                {!addToAllBanks && <span className="generalStyles__inputLabelRequired">*</span>}
              </span>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                placeholder={addToAllBanks ? "All elevator banks" : "Select bank"}
                value={addToAllBanks ? undefined : selectedBankId}
                onChange={handleBankChange}
                // The switch replaces the choice rather than narrowing it, so
                // leaving a bank selected underneath would misreport where the
                // action is going.
                disabled={addToAllBanks}
              >
                {wizardState.bankIds.map((bankId) => (
                  <Select.Option key={bankId} value={bankId}>
                    {wizardState.bankNames?.[bankId] || bankId}
                  </Select.Option>
                ))}
              </Select>
              {!addToAllBanks && derivedLiftType && (
                <span style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                  Lift Type: {derivedLiftType}
                </span>
              )}
            </div>
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">All to all banks</span>
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <Switch
                  checked={addToAllBanks}
                  onChange={handleAddToAllBanksChange}
                  aria-label="All to all banks"
                />
                <span style={{ fontSize: 12, color: "#888" }}>
                  {allElevatorBankIds.length > 0
                    ? `Add this action to every elevator bank (${allElevatorBankIds.length})`
                    : "No elevator banks in this wizard"}
                </span>
              </div>
              {addToAllBanks && (
                <span style={{ fontSize: 12, color: "#888", marginTop: 4, display: "block" }}>
                  {/* The catalog is the only command list that means the same
                      thing across banks — each bank has its own adapter, so an
                      adapter-derived list belongs to one bank only. */}
                  Commands come from the full control catalog; confirm each bank&apos;s controller
                  accepts the command before relying on it.
                </span>
              )}
            </div>
          </div>

          {/* MODBUS catalog-fallback notice — the adapter's binary CDO
              decoded to zero control writes (none configured, or the CDO
              could not be fetched), so the command list below is the full
              Control catalog rather than adapter-verified writes. */}
          {modbusCatalogFallbackActive && (
            <div className="row">
              <div className="col-12 mt-3">
                <Alert
                  type="warning"
                  showIcon
                  message="No control commands were decoded from this MODBUS adapter"
                  description="Its binary CDO template has no configured control writes, or could not be read. You can still add actions from the full command catalog below, but confirm the adapter actually maps each command before relying on it."
                />
              </div>
            </div>
          )}

          {/* Full-catalog escape hatch. This step is the only place a control
              button can be defined — the monitoring panel renders exactly what
              is configured here and nothing else — so an adapter that
              under-reports its commands would otherwise make them permanently
              unreachable. Off by default: the adapter-verified list stays the
              normal path, and turning this on is an explicit, warned choice. */}
          {!isMceBank && !isModbusBank && (
            <div className="row">
              <div className="col-12 mt-3">
                <Checkbox
                  checked={showAllCommands}
                  onChange={(e) => setShowAllCommands(e.target.checked)}
                >
                  Show all control commands, not just those this adapter reports
                </Checkbox>
              </div>
            </div>
          )}

          {manualCatalogOverrideActive && (
            <div className="row">
              <div className="col-12 mt-3">
                <Alert
                  type="warning"
                  showIcon
                  message="Showing the full control catalog"
                  description="The list below includes commands this adapter did not report. Confirm the controller actually accepts a command before adding it — an action bound to an unsupported command renders a button that silently does nothing."
                />
              </div>
            </div>
          )}

          {/* Row 2 — Button Type + Guide */}
          <div className="row">
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">
                Button Type <span className="generalStyles__inputLabelRequired">*</span>
              </span>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                value={bankAction.buttonType}
                onChange={(val: WizardButtonType) =>
                  setBankAction((prev) => {
                    const mirrored = mirrorsToggleCommand(val);
                    // Re-derive what the new type implies for a command that
                    // is already picked, so switching type does not leave ON/OFF
                    // labels or a mirrored off-command from the previous one.
                    const labels = prev.messageType
                      ? autoActionLabels(prev.messageType, val)
                      : { label: prev.label, toggleLabel: prev.toggleLabel };
                    return {
                      ...prev,
                      buttonType: val,
                      label: labels.label,
                      toggleLabel: usesToggleField(val) ? labels.toggleLabel : "",
                      toggleMessageType: mirrored ? prev.messageType : prev.toggleMessageType,
                      toggleCommand: mirrored ? prev.command : prev.toggleCommand,
                    };
                  })
                }
              >
                {WIZARD_BUTTON_TYPE_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                    <ColorDot color={BUTTON_TYPE_DOT_COLOR[opt.value] ?? "#d9d9d9"} />
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">Need Help?</span>
              <div
                role="button"
                tabIndex={0}
                onClick={() => dispatch(updateOpenModalBankActionsHelp(true))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    dispatch(updateOpenModalBankActionsHelp(true));
                  }
                }}
                style={{
                  width: "100%",
                  marginTop: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "5px 12px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                <FontAwesomeIcon icon={faCircleInfo} />
                <span>View Action Button Types Guide</span>
              </div>
            </div>
            <div className="col-12 mt-2">
              <span style={{ fontSize: 12, color: "#888", display: "block" }}>
                {WIZARD_BUTTON_TYPE_OPTIONS.find((o) => o.value === bankAction.buttonType)
                  ?.description ?? ""}
              </span>
            </div>
          </div>
          <BankActionsHelpModal />

          <br />
          <Divider dashed>Action</Divider>

          {/* Row 3 — Control Command + Label */}
          <div className="row">
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">
                Control Command <span className="generalStyles__inputLabelRequired">*</span>
              </span>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                placeholder={getCommandPlaceholder()}
                value={bankAction.messageType}
                onChange={(val: string) => {
                  // Pre-fill both labels from the selected command so the
                  // operator has a sensible starting point — they can still
                  // freely edit them afterwards. Tooltip is no longer a form
                  // field (always sent as "" — see Row 4 below).
                  const picked = resolveControlOptionForCommand(commandOptions, val);
                  setBankAction((prev) => {
                    const labels = autoActionLabels(val, prev.buttonType);
                    const mirrored = mirrorsToggleCommand(prev.buttonType);
                    return {
                      ...prev,
                      messageType: val,
                      // Keep the WHOLE picked option, not just its value —
                      // this is what the POST ships as `command`.
                      command: picked,
                      label: labels.label,
                      // TOGGLE drives both faces with one command, so the off
                      // side follows the on side rather than being chosen.
                      toggleMessageType: mirrored ? val : prev.toggleMessageType,
                      toggleCommand: mirrored ? picked : prev.toggleCommand,
                      toggleLabel: usesToggleField(prev.buttonType)
                        ? labels.toggleLabel
                        : prev.toggleLabel,
                    };
                  });
                }}
                disabled={(!selectedBankId && !addToAllBanks) || noControlKeys}
                showSearch
                optionFilterProp="label"
                notFoundContent="No control commands in this adapter"
              >
                {commandOptions.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">
                Label <span className="generalStyles__inputLabelRequired">*</span>
              </span>
              <Input
                style={{ width: "100%", marginTop: 4 }}
                value={bankAction.label}
                onChange={(e) => updateBankActionField("label", e.target.value)}
              />
            </div>
          </div>

          {/* Row 4 — Timer + Action Color. Timer replaces the old Tooltip
              input (tooltip is no longer operator-editable here — it's
              always sent as ""). Stays visible for every button type so
              the layout doesn't jump around; disabled (rather than
              hidden) when the selected button type doesn't use a timer.
              The help text flips with the selection rather than the
              operator having to re-read the dropdown description. */}
          <div className="row">
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">
                Timer (seconds){" "}
                {needsTimer(bankAction.buttonType) && (
                  <span className="generalStyles__inputLabelRequired">*</span>
                )}
              </span>
              <InputNumber
                style={{ width: "100%", marginTop: 4 }}
                min={needsTimer(bankAction.buttonType) ? 1 : 0}
                value={bankAction.timer}
                disabled={!usesTimerField(bankAction.buttonType)}
                onChange={(val) =>
                  updateBankActionField("timer", typeof val === "number" ? val : 0)
                }
              />
              <span style={{ fontSize: 12, color: "#888", marginTop: 4, display: "block" }}>
                {timerHelpText(bankAction.buttonType)}
              </span>
            </div>
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">Action Color</span>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                placeholder="Select a color"
                value={bankAction.color}
                onChange={(val: string) => updateBankActionField("color", val)}
                allowClear
                onClear={() => updateBankActionField("color", undefined)}
              >
                {ACTION_COLOR_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                    <ColorDot color={opt.value} />
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          {/* Row — Valid States. Messages the runtime checks against the
              elevator's current service mode to auto-disable the button.
              Same semantics/component as the dedicated Bank Actions
              screen; may be left empty.

              NOT scoped to the bank's adapter, and deliberately left that
              way here: Valid States is compared against the car's SERVICE
              MODE, which every adapter reports, and narrowing it would be a
              behaviour change well outside this field. */}
          <div className="row">
            <div className="col-12 mt-3">
              <span className="generalStyles__inputLabel">Valid States</span>
              <div style={{ marginTop: 4 }}>
                <SelectMessagesMultiple
                  inputValue={bankAction.validStates}
                  inputOnChange={(ids) => updateBankActionField("validStates", ids)}
                  disabledInput={false}
                  displaySelect
                  width="100%"
                />
              </div>
            </div>
          </div>

          {/* Row — Expected Response. The messages whose arrival confirms the
              command was acted on, and how long to wait for them. Leaving it
              empty is a valid answer and the common one: the monitoring panel
              then reports a press as "sent", never as confirmed.

              Scoped to the bank's adapter, because nominating a message the
              adapter cannot report guarantees the action reads Unconfirmed
              forever. The scope comes from the adapter keys this step already
              loads for the Control Command dropdown — no second data path. */}
          <div className="row">
            <div className="col-12 col-md-8 mt-3">
              <span className="generalStyles__inputLabel">Expected Response</span>
              <div style={{ marginTop: 4 }}>
                <SelectMessagesMultiple
                  inputValue={bankAction.expectedResponse}
                  inputOnChange={(ids) => updateBankActionField("expectedResponse", ids)}
                  disabledInput={false}
                  displaySelect
                  width="100%"
                  allowedIds={adapterMessageIds}
                />
              </div>
              <span style={{ fontSize: 12, color: "#888", marginTop: 4, display: "block" }}>
                {expectedResponseScopeHelp()}
              </span>
            </div>
            <div className="col-12 col-md-4 mt-3">
              <span className="generalStyles__inputLabel">Verification Timeout (seconds)</span>
              <InputNumber
                style={{ width: "100%", marginTop: 4 }}
                min={0}
                value={bankAction.expectedResponseTimeout}
                disabled={bankAction.expectedResponse.length === 0}
                onChange={(val) =>
                  updateBankActionField(
                    "expectedResponseTimeout",
                    typeof val === "number" && val > 0 ? val : 0,
                  )
                }
              />
              <span style={{ fontSize: 12, color: "#888", marginTop: 4, display: "block" }}>
                Leave at 0 to use the default window.
              </span>
            </div>
          </div>

          {/* Toggle counterpart rows — required for TOGGLE, TOGGLETIMER,
              HOLDBACK, TURNBACK, ENERGIZER_TURN_BACK; optional for
              SWITCH's off-sequence preset. Not shown for SINGLE, which
              is a plain fire-once button with no Toggle Action. */}
          {showToggleActionSection && (
            <>
              <br />
              <Divider dashed>Toggle Action</Divider>
              {/* Row 5 — Toggle Command + Toggle Label */}
              <div className="row">
                <div className="col-12 col-md-6 mt-3">
                  <span className="generalStyles__inputLabel">
                    {toggleCommandLabel(bankAction.buttonType)}{" "}
                    {needsToggle(bankAction.buttonType) && (
                      <span className="generalStyles__inputLabelRequired">*</span>
                    )}
                  </span>
                  <Select
                    style={{ width: "100%", marginTop: 4 }}
                    placeholder={getToggleCommandPlaceholder()}
                    value={bankAction.toggleMessageType}
                    onChange={(val: string) => {
                      // Same pre-fill behavior as the primary Control
                      // Command — operator can still edit afterwards.
                      // Toggle Tooltip is no longer a form field (always
                      // sent as "" — see Row 6 below).
                      const humanized = humanizeControlCommandName(val);
                      setBankAction((prev) => ({
                        ...prev,
                        toggleMessageType: val,
                        // Same as the primary command — the whole option
                        // is what the POST ships as `toggle_command`.
                        toggleCommand: resolveControlOptionForCommand(commandOptions, val),
                        toggleLabel: humanized,
                      }));
                    }}
                    disabled={
                      mirrorsToggleCommand(bankAction.buttonType) ||
                      (!selectedBankId && !addToAllBanks) ||
                      noControlKeys
                    }
                    allowClear={!mirrorsToggleCommand(bankAction.buttonType)}
                    onClear={() =>
                      setBankAction((prev) => ({
                        ...prev,
                        toggleMessageType: undefined,
                        toggleCommand: undefined,
                      }))
                    }
                    showSearch
                    optionFilterProp="label"
                  >
                    {commandOptions.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                  {mirrorsToggleCommand(bankAction.buttonType) && (
                    <span style={{ fontSize: 12, color: "#888", marginTop: 4, display: "block" }}>
                      A Toggle button drives both faces with one command, so this follows the action
                      command above.
                    </span>
                  )}
                </div>
                <div className="col-12 col-md-6 mt-3">
                  <span className="generalStyles__inputLabel">
                    {toggleLabelLabel(bankAction.buttonType)}
                  </span>
                  <Input
                    style={{ width: "100%", marginTop: 4 }}
                    value={bankAction.toggleLabel}
                    onChange={(e) => updateBankActionField("toggleLabel", e.target.value)}
                  />
                </div>
              </div>

              {/* Row 6 — Toggle Timer + Toggle Action Color. Toggle Timer
                  replaces the old Toggle Tooltip input (toggle tooltip is
                  no longer operator-editable here — it's always sent as
                  ""). Stays visible whenever the Toggle Action section is
                  shown; disabled (rather than hidden) for button types
                  that don't require it (only TOGGLETIMER does). */}
              <div className="row">
                <div className="col-12 col-md-6 mt-3">
                  <span className="generalStyles__inputLabel">
                    Toggle Timer (seconds){" "}
                    {needsToggleTimer(bankAction.buttonType) && (
                      <span className="generalStyles__inputLabelRequired">*</span>
                    )}
                  </span>
                  <InputNumber
                    style={{ width: "100%", marginTop: 4 }}
                    min={needsToggleTimer(bankAction.buttonType) ? 1 : 0}
                    value={bankAction.toggleTimer}
                    disabled={!needsToggleTimer(bankAction.buttonType)}
                    onChange={(val) =>
                      updateBankActionField("toggleTimer", typeof val === "number" ? val : 0)
                    }
                  />
                  <span style={{ fontSize: 12, color: "#888", marginTop: 4, display: "block" }}>
                    Pressing the off button locks both buttons for this many seconds.
                  </span>
                </div>
                <div className="col-12 col-md-6 mt-3">
                  <span className="generalStyles__inputLabel">Toggle Action Color</span>
                  <Select
                    style={{ width: "100%", marginTop: 4 }}
                    placeholder="Select a color"
                    value={bankAction.toggleColor}
                    onChange={(val: string) => updateBankActionField("toggleColor", val)}
                    allowClear
                    onClear={() => updateBankActionField("toggleColor", undefined)}
                  >
                    {ACTION_COLOR_OPTIONS.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                        <ColorDot color={opt.value} />
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Row — Toggle Valid States. Same semantics as Valid
                  States above, applied to the off/toggle command. */}
              <div className="row">
                <div className="col-12 mt-3">
                  <span className="generalStyles__inputLabel">Toggle Valid States</span>
                  <div style={{ marginTop: 4 }}>
                    <SelectMessagesMultiple
                      inputValue={bankAction.toggleValidStates}
                      inputOnChange={(ids) => updateBankActionField("toggleValidStates", ids)}
                      disabledInput={false}
                      displaySelect
                      width="100%"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <DrawerRowDivider icon={faUpDown} title="Actions" />
      <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
        <div
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}
        >
          <div>
            {/* Backend stage 10 = Actions. Previously hardcoded as 9
                (pre-ChannelModifiers numbering); operators got the
                Notifications scope on every export from this step. */}
            <WizardExportButton stage={10} orgId={wizardState.organizationId} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button onClick={handleSkip} size="large">
              Skip Actions
            </Button>
            <WizardOpGate op="create">
              <Button
                type="primary"
                onClick={handleAddAction}
                disabled={!isFormValid()}
                loading={isLoadingPost || isAddingToAllBanks}
                size="large"
              >
                + Add Action
              </Button>
            </WizardOpGate>
          </div>
        </div>

        {stepError && (
          <Alert
            type="error"
            showIcon
            message="Action error"
            description={stepError}
            style={{ marginTop: 16 }}
            closable
            onClose={() => setStepError(null)}
          />
        )}
        <GlobalAlert2
          isError={isErrorPost}
          isSuccess={verifiedActionSuccess}
          requestType="POST"
          error={errorPost}
          name="Action"
        />
        <GlobalAlert2
          isError={isErrorPut}
          isSuccess={isSuccessPut}
          requestType="PUT"
          error={errorPut}
          name="Action"
        />
        <GlobalAlert2
          isError={isErrorDelete}
          isSuccess={false}
          requestType="DELETE"
          error={errorDelete}
          name="Action"
        />

        {/* EDIT ACTION MODAL — pre-populated from the row clicked in the
            grouped actions table. Mirrors the manual-add form fields
            (label, command, button type, conditional timer / toggle off
            inputs). Saving dispatches usePutActionMutation and replaces
            the local row on success. */}
        <Modal
          title="Edit Action"
          open={editingRow !== null}
          onCancel={closeEditModal}
          onOk={handleSaveEdit}
          confirmLoading={isLoadingPut}
          okText="Save"
          cancelText="Cancel"
          okButtonProps={{
            disabled:
              !editLabel.trim() ||
              !editMessageType ||
              (needsTimer(editButtonType) && editTimer <= 0) ||
              (needsToggleTimer(editButtonType) && editToggleTimer <= 0) ||
              (needsToggle(editButtonType) && !editToggleMessageType),
          }}
          destroyOnClose
        >
          {editingRow !== null && (
            <div>
              <WizardReadOnlyId label="Action ID" value={editingRow.id} />
              <div style={{ marginBottom: 12 }}>
                <span className="generalStyles__inputLabel">
                  Label <span className="generalStyles__inputLabelRequired">*</span>
                </span>
                <FormInputText
                  inputValue={editLabel}
                  inputLabelName=""
                  inputOnChange={(newValue: string) => setEditLabel(newValue)}
                  inputVariableName="editLabel"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.PAGE}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <span className="generalStyles__inputLabel">
                  Control Command <span className="generalStyles__inputLabelRequired">*</span>
                </span>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  placeholder="Select control command"
                  value={editMessageType}
                  onChange={(val: string) => setEditMessageType(val)}
                  showSearch
                  optionFilterProp="label"
                >
                  {(editCommandOptions.length > 0 ? editCommandOptions : ALL_CONTROL_OPTIONS).map(
                    (opt) => (
                      <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                        {opt.label}
                      </Select.Option>
                    ),
                  )}
                </Select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <span className="generalStyles__inputLabel">Lift (optional)</span>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  placeholder="Select lift"
                  value={editLiftId}
                  onChange={(val: string) => setEditLiftId(val)}
                  allowClear
                  onClear={() => setEditLiftId(undefined)}
                >
                  {editLiftOptions.map(([liftId, liftName]) => (
                    <Select.Option key={liftId} value={liftId}>
                      {liftName}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <span className="generalStyles__inputLabel">
                  Button Type <span className="generalStyles__inputLabelRequired">*</span>
                </span>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  value={editButtonType}
                  onChange={(val: WizardButtonType) => setEditButtonType(val)}
                >
                  {editButtonTypeOptions.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                      <ColorDot color={BUTTON_TYPE_DOT_COLOR[opt.value] ?? "#d9d9d9"} />
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
                <span style={{ fontSize: 12, color: "#888", marginTop: 4, display: "block" }}>
                  {editButtonTypeOptions.find((o) => o.value === editButtonType)?.description ?? ""}
                </span>
              </div>
              {usesTimerField(editButtonType) && (
                <div style={{ marginBottom: 12 }}>
                  <span className="generalStyles__inputLabel">
                    Timer (seconds){" "}
                    {needsTimer(editButtonType) && (
                      <span className="generalStyles__inputLabelRequired">*</span>
                    )}
                  </span>
                  <InputNumber
                    style={{ width: "100%", marginTop: 4 }}
                    min={needsTimer(editButtonType) ? 1 : 0}
                    value={editTimer}
                    onChange={(val) => setEditTimer(typeof val === "number" ? val : 0)}
                  />
                  <span style={{ fontSize: 12, color: "#888", marginTop: 4, display: "block" }}>
                    {timerHelpText(editButtonType)}
                  </span>
                </div>
              )}
              {needsToggleTimer(editButtonType) && (
                <div style={{ marginBottom: 12 }}>
                  <span className="generalStyles__inputLabel">
                    Toggle Timer (seconds){" "}
                    <span className="generalStyles__inputLabelRequired">*</span>
                  </span>
                  <InputNumber
                    style={{ width: "100%", marginTop: 4 }}
                    min={1}
                    value={editToggleTimer}
                    onChange={(val) => setEditToggleTimer(typeof val === "number" ? val : 0)}
                  />
                  <span style={{ fontSize: 12, color: "#888", marginTop: 4, display: "block" }}>
                    Pressing the off button locks both buttons for this many seconds.
                  </span>
                </div>
              )}
              {usesToggleField(editButtonType) && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <span className="generalStyles__inputLabel">
                      {toggleCommandLabel(editButtonType)}{" "}
                      {needsToggle(editButtonType) && (
                        <span className="generalStyles__inputLabelRequired">*</span>
                      )}
                    </span>
                    <Select
                      style={{ width: "100%", marginTop: 4 }}
                      placeholder={
                        needsToggle(editButtonType)
                          ? "Select off-state command"
                          : "Select off-state command (optional)"
                      }
                      value={editToggleMessageType}
                      onChange={(val: string) => setEditToggleMessageType(val)}
                      allowClear
                      onClear={() => setEditToggleMessageType(undefined)}
                      showSearch
                      optionFilterProp="label"
                    >
                      {(editCommandOptions.length > 0
                        ? editCommandOptions
                        : ALL_CONTROL_OPTIONS
                      ).map((opt) => (
                        <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <span className="generalStyles__inputLabel">
                      {toggleLabelLabel(editButtonType)}
                    </span>
                    <FormInputText
                      inputValue={editToggleLabel}
                      inputLabelName=""
                      inputOnChange={(newValue: string) => setEditToggleLabel(newValue)}
                      inputVariableName="editToggleLabel"
                      isRequired={false}
                      disabledInput={false}
                      formularyType={FormularyType.PAGE}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </Modal>

        {/* VISUAL CONFIG MODAL — edit color, valid_states, timers on the
            matching action_bank row. Read-only fields (action_id /
            toggle_action_id / message_id / button_type) come from the
            looked-up record and are managed elsewhere; this modal only
            touches the visual / timing surface. */}
        <Modal
          title="Visual Configuration"
          open={visualModalOpen}
          onCancel={() => {
            setVisualModalOpen(false);
            setEditingVisualRowId(null);
            setVisualError(null);
          }}
          onOk={handleSaveVisual}
          okText="Save Changes"
          confirmLoading={visualSaving}
          width={720}
          destroyOnClose
        >
          {visualError && (
            <Alert type="error" showIcon message={visualError} style={{ marginBottom: 12 }} />
          )}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Color, valid states, and timer fields are stored on the action_bank record. Button type and command linkage are managed via the Edit modal."
          />
          <div className="row">
            <div className="col-12 col-md-6 mb-3">
              <span className="generalStyles__inputLabel">Label</span>
              <Input
                placeholder="On-state button label"
                value={visualLabel}
                onChange={(e) => setVisualLabel(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-6 mb-3">
              <span className="generalStyles__inputLabel">Toggle Label</span>
              <Input
                placeholder="Off-state button label"
                value={visualToggleLabel}
                onChange={(e) => setVisualToggleLabel(e.target.value)}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-4 mb-3">
              <span className="generalStyles__inputLabel">Color</span>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <Input
                  placeholder="#RRGGBB"
                  value={visualColor}
                  onChange={(e) => setVisualColor(e.target.value)}
                  status={validateHexColor(visualColor) ? "error" : undefined}
                />
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 4,
                    border: "1px solid #d9d9d9",
                    backgroundColor: validateHexColor(visualColor) ? "transparent" : visualColor,
                  }}
                />
              </div>
              {validateHexColor(visualColor) && (
                <span style={{ fontSize: 12, color: "#ff4d4f" }}>
                  {validateHexColor(visualColor)}
                </span>
              )}
            </div>
            <div className="col-12 col-md-4 mb-3">
              <span className="generalStyles__inputLabel">Color 2</span>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <Input
                  placeholder="#RRGGBB"
                  value={visualColor2}
                  onChange={(e) => setVisualColor2(e.target.value)}
                  status={validateHexColor(visualColor2) ? "error" : undefined}
                />
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 4,
                    border: "1px solid #d9d9d9",
                    backgroundColor: validateHexColor(visualColor2) ? "transparent" : visualColor2,
                  }}
                />
              </div>
              {validateHexColor(visualColor2) && (
                <span style={{ fontSize: 12, color: "#ff4d4f" }}>
                  {validateHexColor(visualColor2)}
                </span>
              )}
            </div>
            <div className="col-12 col-md-4 mb-3">
              <span className="generalStyles__inputLabel">Toggle Color</span>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <Input
                  placeholder="#RRGGBB"
                  value={visualToggleColor}
                  onChange={(e) => setVisualToggleColor(e.target.value)}
                  status={validateHexColor(visualToggleColor) ? "error" : undefined}
                />
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 4,
                    border: "1px solid #d9d9d9",
                    backgroundColor: validateHexColor(visualToggleColor)
                      ? "transparent"
                      : visualToggleColor,
                  }}
                />
              </div>
              {validateHexColor(visualToggleColor) && (
                <span style={{ fontSize: 12, color: "#ff4d4f" }}>
                  {validateHexColor(visualToggleColor)}
                </span>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-4 mb-3">
              <span className="generalStyles__inputLabel">Timer (s)</span>
              <InputNumber
                min={0}
                value={visualTimer}
                onChange={(v) => setVisualTimer(typeof v === "number" ? v : 0)}
                style={{ width: "100%" }}
              />
            </div>
            <div className="col-12 col-md-4 mb-3">
              <span className="generalStyles__inputLabel">Timer 2 (s)</span>
              <InputNumber
                min={0}
                value={visualTimer2}
                onChange={(v) => setVisualTimer2(typeof v === "number" ? v : 0)}
                style={{ width: "100%" }}
              />
            </div>
            <div className="col-12 col-md-4 mb-3">
              <span className="generalStyles__inputLabel">Toggle Timer (s)</span>
              <InputNumber
                min={0}
                value={visualToggleTimer}
                onChange={(v) => setVisualToggleTimer(typeof v === "number" ? v : 0)}
                style={{ width: "100%" }}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-6 mb-3">
              <span className="generalStyles__inputLabel">Valid States</span>
              <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="e.g. INDEPENDENT, FIRE_SERVICE"
                value={visualValidStates}
                onChange={(v: string[]) => setVisualValidStates(v)}
              />
            </div>
            <div className="col-12 col-md-6 mb-3">
              <span className="generalStyles__inputLabel">Toggle Valid States</span>
              <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="e.g. NORMAL"
                value={visualToggleValidStates}
                onChange={(v: string[]) => setVisualToggleValidStates(v)}
              />
            </div>
          </div>
        </Modal>
        {/* ── Scheduled Actions ──────────────────────────── */}
        <Divider orientation="left">Scheduled Actions</Divider>
        <WizardScheduledActions configuredControlOptions={scheduledControlOptions} />
      </div>
    </Spin>
  );
}
